/*
Auto-generated by: https://github.com/pmndrs/gltfjsx
Command: npx gltfjsx@6.4.1 public/assets/test_10.gltf --keepnames --keepgroups --bones --meta --keepmeshes --keepmaterials 
*/

import React, { useRef, useEffect } from "react";
import { useFrame } from "@react-three/fiber";
import { useGLTF, useAnimations } from "@react-three/drei";
import * as THREE from "three";
import { assets_prefix } from "./constants";

const whiteColor = new THREE.Color("#FFFFFF");

const RENDER_ORDERS = {
  //   "1 Substrate": 27,
  //   "2 Nwell": 26,
  //   "4 Poly": 25,
  //   "5 Licon": 24,
  //   "6 Li1": 23,
  //   "8 Met1": 22,
  //   "9 Via": 21,
  //   "10 Met2": 20,
  //   "12 Met3": 19,
  //   "13 Via3": 18,
  //   "14 Met4": 17,
  //   "16 Tap": 16,

  //   "1 Substrate Zoomed": 15,
  //   "2 Nwell Zoomed": 14,
  //   "4 Poly Zoomed": 13,
  //   "5 Licon Zoomed": 12,
  //   "6 Li1 Zoomed": 11,
  //   "8 Met1 Zoomed": 10,
  //   "9 Via Zoomed": 9,
  //   "10 Met2 Zoomed": 8,
  //   "12 Met3 Zoomed": 7,
  //   "13 Via3 Zoomed": 6,
  //   "14 Met4 Zoomed": 5,
  //   "16 Tap Zoomed": 4,

  "1 Substrate": 4,
  "2 Nwell": 5,
  "4 Poly": 6,
  "5 Licon": 7,
  "6 Li1": 8,
  "8 Met1": 9,
  "9 Via": 10,
  "10 Met2": 11,
  "12 Met3": 12,
  "13 Via3": 13,
  "14 Met4": 14,
  "16 Tap": 15,

  "1 Substrate Zoomed": 16,
  "2 Nwell Zoomed": 17,
  "4 Poly Zoomed": 18,
  "5 Licon Zoomed": 19,
  "6 Li1 Zoomed": 20,
  "8 Met1 Zoomed": 21,
  "9 Via Zoomed": 22,
  "10 Met2 Zoomed": 23,
  "12 Met3 Zoomed": 24,
  "13 Via3 Zoomed": 25,
  "14 Met4 Zoomed": 26,
  "16 Tap Zoomed": 27,

  "Red Waves": 3,
  "Red Waves Top": 29,
  "Blue Waves": 2,
  "Blue Waves Top": 30,
  "Atum A5 Signal": 4,
};

export function Model(props) {
  const { topLevelRef } = props;

  const { nodes, materials, animations } = useGLTF(
    assets_prefix + "/test_10.gltf",
    true,
    true,
  );
  const { actions } = useAnimations(animations, topLevelRef);

  const enclosure1Ref = useRef();
  const enclosure2Ref = useRef();
  const redWaves = useRef();
  const redWavesTop = useRef();
  const foamBackRef = useRef();

  const flicker5Ref = useRef(); // 10 Met2 Zoomed
  const fuseTopRef = useRef();
  const redHueRef1 = useRef();
  const redHueRef2 = useRef();
  const redHueRef3 = useRef();
  const foamRef = useRef();
  const needleFailRef = useRef();

  const needleProgressRef = useRef(0);

  // The interactive demo controls (unplug, die cover, breach, disable, foam,
  // radio, enclosure) were retired with their store setters, so every flag
  // they read sat frozen at its initial value. Each animation below plays
  // that resting state once per `actions`.
  React.useEffect(() => {
    const unplug = actions["Action"];
    if (unplug) {
      unplug.reset();
      unplug.time = unplug.getClip().duration;
      unplug.timeScale = -1;
      unplug.clampWhenFinished = true;
      unplug.setLoop(THREE.LoopOnce);
      unplug.play();
    }
  }, [actions]);

  useEffect(() => {
    const fuseBreaking1 = actions["Fuse Breaking 1Action"];
    const fuseBreaking2 = actions["Fuse Breaking 2Action"];

    if (fuseBreaking1) {
      fuseBreaking1.reset();
      fuseBreaking1.clampWhenFinished = true;
      fuseBreaking1.setLoop(THREE.LoopOnce);
      fuseBreaking1.play();
      fuseBreaking1.time = 0.4;
    }

    if (fuseBreaking2) {
      fuseBreaking2.reset();
      fuseBreaking2.clampWhenFinished = true;
      fuseBreaking2.setLoop(THREE.LoopOnce);
      fuseBreaking2.time = 0.4;
      fuseBreaking2.play();
    }
  }, [actions]);

  useEffect(() => {
    const dieCover = actions["Atum A5 FlapAction"];
    if (dieCover) {
      dieCover.reset();
      dieCover.timeScale = -1;
      dieCover.clampWhenFinished = true;
      dieCover.setLoop(THREE.LoopOnce);
      dieCover.time = dieCover.getClip().duration;

      dieCover.play();
    }
  }, [actions]);

  useEffect(() => {
    const needleBreach = actions["NeedleAction.001"];
    const needleFail = actions["Needle FailAction"];

    if (needleBreach && needleFail) {
      needleBreach.stop();
    }
  }, [actions]);

  useEffect(() => {
    const fan1 = actions["Fan 1Action"];
    const fan2 = actions["Fan 2Action"];
    const fan3 = actions["Fan 3Action"];
    if (fan1) {
      fan1.play();
    }

    if (fan2) {
      fan2.play();
    }
    if (fan3) {
      fan3.play();
    }
  }, [actions]);

  useEffect(() => {
    const setVisibility = (ref, isVisible) => {
      if (ref.current) {
        ref.current.visible = isVisible;
      }
    };

    setVisibility(redWaves, false);
    setVisibility(redWavesTop, false);

    setVisibility(enclosure1Ref, false);
    setVisibility(enclosure2Ref, false);

    setVisibility(foamRef, false);
    setVisibility(foamBackRef, false);

    setVisibility(needleProgressRef, false);
    setVisibility(needleFailRef, false);
  }, []);

  useFrame(() => {
    fuseTopRef.current?.children.forEach((c) => {
      c.material.transparent = true;
      c.material.opacity = 1;
      c.material.needsUpdate = true;
    });
    redHueRef2.current.material.color = whiteColor;
    redHueRef2.current.material.needsUpdate = true;
    redHueRef3.current.material.color = whiteColor;
    redHueRef3.current.material.needsUpdate = true;
    redHueRef1.current.material.color = whiteColor;
    redHueRef1.current.material.needsUpdate = true;
    flicker5Ref.current.material.color = whiteColor;
    flicker5Ref.current.material.needsUpdate = true;
    if (enclosure1Ref.current && enclosure2Ref.current) {
      enclosure1Ref.current.visible = false;
      enclosure2Ref.current.visible = false;
    }
  });

  return (
    <group ref={topLevelRef} {...props} dispose={null}>
      <group name="Scene">
        <group
          name="Fans"
          position={[0, 10.074, 0]}
          userData={{ name: "Fans" }}
        >
          <mesh
            name="Fan_1"
            geometry={nodes.Fan_1.geometry}
            material={materials.Fans}
            position={[2.171, 1, 3.723]}
            scale={5}
            userData={{ name: "Fan 1" }}
          />
          <mesh
            name="Fan_2"
            geometry={nodes.Fan_2.geometry}
            material={materials.Fans}
            position={[-2.136, 1, 3.723]}
            scale={5}
            userData={{ name: "Fan 2" }}
          />
          <mesh
            name="Fan_3"
            geometry={nodes.Fan_3.geometry}
            material={materials.Fans}
            position={[-6.443, 1, 3.723]}
            scale={5}
            userData={{ name: "Fan 3" }}
          />
        </group>
        <group
          name="Case_&_Front"
          position={[0, 10.074, 0]}
          userData={{ name: "Case & Front" }}
        >
          <group
            name="Battery_1"
            position={[6.339, -0.227, 8.192]}
            userData={{ name: "Battery 1" }}
          >
            <mesh
              name="Battery_1_1"
              geometry={nodes.Battery_1_1.geometry}
              material={materials.Blue}
            />
            <mesh
              name="Battery_1_2"
              geometry={nodes.Battery_1_2.geometry}
              material={materials["White Plastic"]}
            />
            <mesh
              name="Battery_1_3"
              geometry={nodes.Battery_1_3.geometry}
              material={materials["Yellow Plastic"]}
            />
            <mesh
              name="Battery_1_4"
              geometry={nodes.Battery_1_4.geometry}
              material={materials["Metal 1"]}
            />
            <mesh
              name="Battery_1_5"
              geometry={nodes.Battery_1_5.geometry}
              material={materials["Metal 3"]}
            />
            <mesh
              name="Battery_1_6"
              geometry={nodes.Battery_1_6.geometry}
              material={materials["Blue Plastic"]}
            />
          </group>
          <group
            name="Battery_2"
            position={[1.269, -0.227, 8.192]}
            userData={{ name: "Battery 2" }}
          >
            <mesh
              name="Battery_2_1"
              geometry={nodes.Battery_2_1.geometry}
              material={materials.Blue}
            />
            <mesh
              name="Battery_2_2"
              geometry={nodes.Battery_2_2.geometry}
              material={materials["White Plastic"]}
            />
            <mesh
              name="Battery_2_3"
              geometry={nodes.Battery_2_3.geometry}
              material={materials["Yellow Plastic"]}
            />
            <mesh
              name="Battery_2_4"
              geometry={nodes.Battery_2_4.geometry}
              material={materials["Metal 1"]}
            />
            <mesh
              name="Battery_2_5"
              geometry={nodes.Battery_2_5.geometry}
              material={materials["Metal 3"]}
            />
            <mesh
              name="Battery_2_6"
              geometry={nodes.Battery_2_6.geometry}
              material={materials["Blue Plastic"]}
            />
            <mesh
              name="Battery_2_7"
              geometry={nodes.Battery_2_7.geometry}
              material={materials.Silver}
            />
          </group>
          <mesh
            name="Battery_Case"
            geometry={nodes.Battery_Case.geometry}
            material={materials["Chip Black"]}
            position={[6.543, -0.852, 8.589]}
            rotation={[0, -Math.PI / 2, 0]}
            scale={0.306}
            userData={{ name: "Battery Case" }}
          />
          {
            <mesh
              ref={enclosure2Ref}
              visible={false}
              name="Battery_Case_Lid"
              geometry={nodes.Battery_Case_Lid.geometry}
              material={materials["Chip Black"]}
              position={[6.543, -0.852, 8.589]}
              rotation={[0, -Math.PI / 2, 0]}
              scale={0.306}
              userData={{ name: "Battery Case Lid" }}
            />
          }

          {/* <mesh
            name="Blue_Shield_Waves"
            geometry={nodes.Blue_Shield_Waves.geometry}
            material={materials["Blue Waves"]}
            position={[0, 1, 0]}
            userData={{ name: "Blue Shield Waves" }}
            visible={false}
            renderOrder={RENDER_ORDERS["Blue Waves"]}
          ></mesh>

          <mesh
            name="Blue_Shield_Waves_Top"
            geometry={nodes.Blue_Shield_Waves_Top.geometry}
            material={materials["Blue Waves"]}
            position={[0, 1, 0]}
            userData={{ name: "Blue Shield Waves Top" }}
            visible={false}
            renderOrder={RENDER_ORDERS["Blue Waves Top"]}
          ></mesh> */}
          <group name="Case" position={[0, 1, 0]} userData={{ name: "Case" }}>
            <mesh
              name="Case_1"
              geometry={nodes.Case_1.geometry}
              material={materials["Metal 1"]}
            />
            <mesh
              name="Case_2"
              geometry={nodes.Case_2.geometry}
              material={materials["Metal 2"]}
            />
            <mesh
              name="Case_3"
              geometry={nodes.Case_3.geometry}
              material={materials["Metal 1 Holes 1"]}
            />
            <mesh
              name="Case_4"
              geometry={nodes.Case_4.geometry}
              material={materials["Metal 1 Holes 2"]}
            />
            <mesh
              name="Case_5"
              geometry={nodes.Case_5.geometry}
              material={materials.Black}
            />
            <mesh
              name="Case_6"
              geometry={nodes.Case_6.geometry}
              material={materials.Screws}
            />
            <mesh
              name="Case_7"
              geometry={nodes.Case_7.geometry}
              material={materials.Fans}
            />
            <mesh
              name="Case_8"
              geometry={nodes.Case_8.geometry}
              material={materials["Enclosure Back"]}
            />
          </group>
          <group name="Front" position={[0, 1, 0]} userData={{ name: "Front" }}>
            <mesh
              name="Front_1"
              geometry={nodes.Front_1.geometry}
              material={materials["Metal 1"]}
            />
            <mesh
              name="Front_2"
              geometry={nodes.Front_2.geometry}
              material={materials["Metal 3"]}
            />
            <mesh
              name="Front_3"
              geometry={nodes.Front_3.geometry}
              material={materials["Metal 3 Holes 1"]}
            />
            <mesh
              name="Front_4"
              geometry={nodes.Front_4.geometry}
              material={materials.Red}
            />
            <mesh
              name="Front_5"
              geometry={nodes.Front_5.geometry}
              material={materials.Green}
            />
            <mesh
              name="Front_6"
              geometry={nodes.Front_6.geometry}
              material={materials.Yellow}
            />
            <mesh
              name="Front_7"
              geometry={nodes.Front_7.geometry}
              material={materials["Blue Plastic"]}
            />
          </group>
          <mesh
            ref={foamRef}
            name="Metal_Foam"
            geometry={nodes.Metal_Foam.geometry}
            material={materials["Metal Foam"]}
            position={[0, 1, 0]}
            userData={{ name: "Metal Foam" }}
          />

          <mesh
            ref={foamBackRef}
            name="Metal_Foam_Back"
            geometry={nodes.Metal_Foam_Back.geometry}
            material={materials["Metal Foam"]}
            position={[0, 1, 0]}
            userData={{ name: "Metal Foam Back" }}
          />

          <mesh
            name="Red_Shield_Waves"
            geometry={nodes.Red_Shield_Waves.geometry}
            material={materials["Red Waves"]}
            position={[0, 1, 0]}
            userData={{ name: "Red Shield Waves" }}
            ref={redWaves}
            visible={false}
            renderOrder={RENDER_ORDERS["Red Waves"]}
          />

          <mesh
            name="Red_Shield_Waves_Top"
            geometry={nodes.Red_Shield_Waves_Top.geometry}
            material={materials["Red Waves"]}
            position={[0, 1, 0]}
            userData={{ name: "Red Shield Waves Top" }}
            ref={redWavesTop}
            visible={false}
            renderOrder={RENDER_ORDERS["Red Waves Top"]}
          />
        </group>

        <group
          ref={enclosure1Ref}
          visible={false}
          name="Lid_"
          position={[0, 10.074, 0]}
          userData={{ name: "Lid " }}
        >
          <group
            name="Lid"
            position={[0, 1, 0]}
            scale={5}
            userData={{ name: "Lid" }}
          >
            <mesh
              name="Lid_1"
              geometry={nodes.Lid_1.geometry}
              material={materials["Lid Metal"]}
            />
            <mesh
              name="Lid_2"
              geometry={nodes.Lid_2.geometry}
              material={materials["Lid Metal Holes"]}
            />
          </group>
        </group>

        <group
          name="Motherboard_Elements"
          position={[0, 10.074, 0]}
          userData={{ name: "Motherboard Elements" }}
        >
          <group
            name="Motherboard_Chips"
            position={[0, -1, 0]}
            userData={{ name: "Motherboard Chips" }}
          >
            <group
              name="Chip_1016"
              position={[1.445, 0.3, -3.303]}
              rotation={[Math.PI, 0, Math.PI]}
              userData={{ name: "Chip 1.016" }}
            >
              <mesh
                name="Chip_1016_1"
                geometry={nodes.Chip_1016_1.geometry}
                material={materials["Chip Black"]}
              />
              <mesh
                name="Chip_1016_2"
                geometry={nodes.Chip_1016_2.geometry}
                material={materials["Chip Yellow"]}
              />
              <mesh
                name="Chip_1016_3"
                geometry={nodes.Chip_1016_3.geometry}
                material={materials["Chip Metal"]}
              />
            </group>
            <group
              name="Chip_1017"
              position={[2.582, 0.3, -3.315]}
              rotation={[Math.PI, 0, Math.PI]}
              userData={{ name: "Chip 1.017" }}
            >
              <mesh
                name="Chip_1017_1"
                geometry={nodes.Chip_1017_1.geometry}
                material={materials["Chip Black"]}
              />
              <mesh
                name="Chip_1017_2"
                geometry={nodes.Chip_1017_2.geometry}
                material={materials["Chip Yellow"]}
              />
              <mesh
                name="Chip_1017_3"
                geometry={nodes.Chip_1017_3.geometry}
                material={materials["Chip Metal"]}
              />
            </group>
            <group
              name="Chip_1018"
              position={[4.104, 0.3, -3.326]}
              rotation={[Math.PI, 0, Math.PI]}
              userData={{ name: "Chip 1.018" }}
            >
              <mesh
                name="Chip_1018_1"
                geometry={nodes.Chip_1018_1.geometry}
                material={materials["Chip Black"]}
              />
              <mesh
                name="Chip_1018_2"
                geometry={nodes.Chip_1018_2.geometry}
                material={materials["Chip Yellow"]}
              />
              <mesh
                name="Chip_1018_3"
                geometry={nodes.Chip_1018_3.geometry}
                material={materials["Chip Metal"]}
              />
            </group>
            <group
              name="Chip_1019"
              position={[-3.992, 0.3, -10.278]}
              rotation={[Math.PI, 0, Math.PI]}
              userData={{ name: "Chip 1.019" }}
            >
              <mesh
                name="Chip_1019_1"
                geometry={nodes.Chip_1019_1.geometry}
                material={materials["Chip Black"]}
              />
              <mesh
                name="Chip_1019_2"
                geometry={nodes.Chip_1019_2.geometry}
                material={materials["Chip Yellow"]}
              />
              <mesh
                name="Chip_1019_3"
                geometry={nodes.Chip_1019_3.geometry}
                material={materials["Chip Metal"]}
              />
            </group>
            <group
              name="Chip_1020"
              position={[7.146, 0.3, -8.71]}
              rotation={[Math.PI, 0, Math.PI]}
              userData={{ name: "Chip 1.020" }}
            >
              <mesh
                name="Chip_1020_1"
                geometry={nodes.Chip_1020_1.geometry}
                material={materials["Chip Black"]}
              />
              <mesh
                name="Chip_1020_2"
                geometry={nodes.Chip_1020_2.geometry}
                material={materials["Chip Yellow"]}
              />
              <mesh
                name="Chip_1020_3"
                geometry={nodes.Chip_1020_3.geometry}
                material={materials["Chip Metal"]}
              />
            </group>
            <group
              name="Chip_3"
              position={[6.656, 0.3, -5.651]}
              userData={{ name: "Chip 3" }}
            >
              <mesh
                name="Chip_3_1"
                geometry={nodes.Chip_3_1.geometry}
                material={materials["Chip Black"]}
              />
              <mesh
                name="Chip_3_2"
                geometry={nodes.Chip_3_2.geometry}
                material={materials["Chip Metal"]}
              />
            </group>
            <group
              name="Chip_3001"
              position={[6.656, 0.3, -5.361]}
              userData={{ name: "Chip 3.001" }}
            >
              <mesh
                name="Chip_3001_1"
                geometry={nodes.Chip_3001_1.geometry}
                material={materials["Chip Black"]}
              />
              <mesh
                name="Chip_3001_2"
                geometry={nodes.Chip_3001_2.geometry}
                material={materials["Chip Metal"]}
              />
            </group>
            <group
              name="Chip_3002"
              position={[8.516, 0.3, -4.495]}
              userData={{ name: "Chip 3.002" }}
            >
              <mesh
                name="Chip_3002_1"
                geometry={nodes.Chip_3002_1.geometry}
                material={materials["Chip Black"]}
              />
              <mesh
                name="Chip_3002_2"
                geometry={nodes.Chip_3002_2.geometry}
                material={materials["Chip Metal"]}
              />
            </group>
            <group
              name="Chip_3003"
              position={[8.243, 0.3, -4.495]}
              userData={{ name: "Chip 3.003" }}
            >
              <mesh
                name="Chip_3003_1"
                geometry={nodes.Chip_3003_1.geometry}
                material={materials["Chip Black"]}
              />
              <mesh
                name="Chip_3003_2"
                geometry={nodes.Chip_3003_2.geometry}
                material={materials["Chip Metal"]}
              />
            </group>
            <group
              name="Chip_4"
              position={[8.167, 0.3, -4.971]}
              userData={{ name: "Chip 4" }}
            >
              <mesh
                name="Chip_4_1"
                geometry={nodes.Chip_4_1.geometry}
                material={materials["Chip Black"]}
              />
              <mesh
                name="Chip_4_2"
                geometry={nodes.Chip_4_2.geometry}
                material={materials["Chip Yellow"]}
              />
              <mesh
                name="Chip_4_3"
                geometry={nodes.Chip_4_3.geometry}
                material={materials["Chip Metal"]}
              />
            </group>
            <group
              name="Chip_4001"
              position={[8.343, 0.3, -4.971]}
              userData={{ name: "Chip 4.001" }}
            >
              <mesh
                name="Chip_4001_1"
                geometry={nodes.Chip_4001_1.geometry}
                material={materials["Chip Black"]}
              />
              <mesh
                name="Chip_4001_2"
                geometry={nodes.Chip_4001_2.geometry}
                material={materials["Chip Yellow"]}
              />
              <mesh
                name="Chip_4001_3"
                geometry={nodes.Chip_4001_3.geometry}
                material={materials["Chip Metal"]}
              />
            </group>
            <group
              name="Chip_Link"
              position={[3.551, 0.3, -2.853]}
              userData={{ name: "Chip Link" }}
            >
              <mesh
                name="Chip_Link_1"
                geometry={nodes.Chip_Link_1.geometry}
                material={materials["Chip Yellow"]}
              />
              <mesh
                name="Chip_Link_2"
                geometry={nodes.Chip_Link_2.geometry}
                material={materials["Chip Metal"]}
              />
            </group>
            <group
              name="Chip_Pin_1"
              position={[3.202, 0.352, -3.252]}
              userData={{ name: "Chip Pin 1" }}
            >
              <mesh
                name="Chip_Pin_1_1"
                geometry={nodes.Chip_Pin_1_1.geometry}
                material={materials["Chip Black"]}
              />
              <mesh
                name="Chip_Pin_1_2"
                geometry={nodes.Chip_Pin_1_2.geometry}
                material={materials["Chip Gold"]}
              />
            </group>
            <group
              name="Chip_Pin_1001"
              position={[3.32, 0.352, -3.252]}
              userData={{ name: "Chip Pin 1.001" }}
            >
              <mesh
                name="Chip_Pin_1001_1"
                geometry={nodes.Chip_Pin_1001_1.geometry}
                material={materials["Chip Black"]}
              />
              <mesh
                name="Chip_Pin_1001_2"
                geometry={nodes.Chip_Pin_1001_2.geometry}
                material={materials["Chip Gold"]}
              />
            </group>
            <group
              name="Chip_Pin_1002"
              position={[4.725, 0.352, -3.252]}
              userData={{ name: "Chip Pin 1.002" }}
            >
              <mesh
                name="Chip_Pin_1002_1"
                geometry={nodes.Chip_Pin_1002_1.geometry}
                material={materials["Chip Black"]}
              />
              <mesh
                name="Chip_Pin_1002_2"
                geometry={nodes.Chip_Pin_1002_2.geometry}
                material={materials["Chip Gold"]}
              />
            </group>
            <group
              name="Chip_Pin_1003"
              position={[4.842, 0.352, -3.252]}
              userData={{ name: "Chip Pin 1.003" }}
            >
              <mesh
                name="Chip_Pin_1003_1"
                geometry={nodes.Chip_Pin_1003_1.geometry}
                material={materials["Chip Black"]}
              />
              <mesh
                name="Chip_Pin_1003_2"
                geometry={nodes.Chip_Pin_1003_2.geometry}
                material={materials["Chip Gold"]}
              />
            </group>
            <group
              name="Chip_Pin_1004"
              position={[2.054, 0.352, -3.252]}
              userData={{ name: "Chip Pin 1.004" }}
            >
              <mesh
                name="Chip_Pin_1004_1"
                geometry={nodes.Chip_Pin_1004_1.geometry}
                material={materials["Chip Black"]}
              />
              <mesh
                name="Chip_Pin_1004_2"
                geometry={nodes.Chip_Pin_1004_2.geometry}
                material={materials["Chip Gold"]}
              />
            </group>
            <group
              name="Chip_Pin_1005"
              position={[2.172, 0.352, -3.252]}
              userData={{ name: "Chip Pin 1.005" }}
            >
              <mesh
                name="Chip_Pin_1005_1"
                geometry={nodes.Chip_Pin_1005_1.geometry}
                material={materials["Chip Black"]}
              />
              <mesh
                name="Chip_Pin_1005_2"
                geometry={nodes.Chip_Pin_1005_2.geometry}
                material={materials["Chip Gold"]}
              />
            </group>
            <group
              name="Chip_Pin_1006"
              position={[0.93, 0.352, -3.127]}
              userData={{ name: "Chip Pin 1.006" }}
            >
              <mesh
                name="Chip_Pin_1006_1"
                geometry={nodes.Chip_Pin_1006_1.geometry}
                material={materials["Chip Black"]}
              />
              <mesh
                name="Chip_Pin_1006_2"
                geometry={nodes.Chip_Pin_1006_2.geometry}
                material={materials["Chip Gold"]}
              />
            </group>
            <group
              name="Chip_Pin_1007"
              position={[1.047, 0.352, -3.127]}
              userData={{ name: "Chip Pin 1.007" }}
            >
              <mesh
                name="Chip_Pin_1007_1"
                geometry={nodes.Chip_Pin_1007_1.geometry}
                material={materials["Chip Black"]}
              />
              <mesh
                name="Chip_Pin_1007_2"
                geometry={nodes.Chip_Pin_1007_2.geometry}
                material={materials["Chip Gold"]}
              />
            </group>
            <group
              name="Chip_Pin_1008"
              position={[0.812, 0.352, -3.127]}
              userData={{ name: "Chip Pin 1.008" }}
            >
              <mesh
                name="Chip_Pin_1008_1"
                geometry={nodes.Chip_Pin_1008_1.geometry}
                material={materials["Chip Black"]}
              />
              <mesh
                name="Chip_Pin_1008_2"
                geometry={nodes.Chip_Pin_1008_2.geometry}
                material={materials["Chip Gold"]}
              />
            </group>
            <group
              name="Chip_Pin_1009"
              position={[0.823, 0.352, -4.08]}
              userData={{ name: "Chip Pin 1.009" }}
            >
              <mesh
                name="Chip_Pin_1009_1"
                geometry={nodes.Chip_Pin_1009_1.geometry}
                material={materials["Chip Black"]}
              />
              <mesh
                name="Chip_Pin_1009_2"
                geometry={nodes.Chip_Pin_1009_2.geometry}
                material={materials["Chip Gold"]}
              />
            </group>
            <group
              name="Chip_Pin_1010"
              position={[0.941, 0.352, -4.08]}
              userData={{ name: "Chip Pin 1.010" }}
            >
              <mesh
                name="Chip_Pin_1010_1"
                geometry={nodes.Chip_Pin_1010_1.geometry}
                material={materials["Chip Black"]}
              />
              <mesh
                name="Chip_Pin_1010_2"
                geometry={nodes.Chip_Pin_1010_2.geometry}
                material={materials["Chip Gold"]}
              />
            </group>
            <group
              name="Chip_Pin_1011"
              position={[1.021, 0.352, -3.859]}
              rotation={[0, -1.571, 0]}
              userData={{ name: "Chip Pin 1.011" }}
            >
              <mesh
                name="Chip_Pin_1011_1"
                geometry={nodes.Chip_Pin_1011_1.geometry}
                material={materials["Chip Black"]}
              />
              <mesh
                name="Chip_Pin_1011_2"
                geometry={nodes.Chip_Pin_1011_2.geometry}
                material={materials["Chip Gold"]}
              />
            </group>
            <group
              name="Chip_Pin_1012"
              position={[1.021, 0.352, -3.741]}
              rotation={[0, -1.571, 0]}
              userData={{ name: "Chip Pin 1.012" }}
            >
              <mesh
                name="Chip_Pin_1012_1"
                geometry={nodes.Chip_Pin_1012_1.geometry}
                material={materials["Chip Black"]}
              />
              <mesh
                name="Chip_Pin_1012_2"
                geometry={nodes.Chip_Pin_1012_2.geometry}
                material={materials["Chip Gold"]}
              />
            </group>
            <group
              name="Chip_Pin_1013"
              position={[0.123, 0.352, -4.08]}
              userData={{ name: "Chip Pin 1.013" }}
            >
              <mesh
                name="Chip_Pin_1013_1"
                geometry={nodes.Chip_Pin_1013_1.geometry}
                material={materials["Chip Black"]}
              />
              <mesh
                name="Chip_Pin_1013_2"
                geometry={nodes.Chip_Pin_1013_2.geometry}
                material={materials["Chip Gold"]}
              />
            </group>
            <group
              name="Chip_Pin_1014"
              position={[0.24, 0.352, -4.08]}
              userData={{ name: "Chip Pin 1.014" }}
            >
              <mesh
                name="Chip_Pin_1014_1"
                geometry={nodes.Chip_Pin_1014_1.geometry}
                material={materials["Chip Black"]}
              />
              <mesh
                name="Chip_Pin_1014_2"
                geometry={nodes.Chip_Pin_1014_2.geometry}
                material={materials["Chip Gold"]}
              />
            </group>
            <group
              name="Chip_Pin_1015"
              position={[6.809, 0.352, -3.143]}
              userData={{ name: "Chip Pin 1.015" }}
            >
              <mesh
                name="Chip_Pin_1015_1"
                geometry={nodes.Chip_Pin_1015_1.geometry}
                material={materials["Chip Black"]}
              />
              <mesh
                name="Chip_Pin_1015_2"
                geometry={nodes.Chip_Pin_1015_2.geometry}
                material={materials["Chip Gold"]}
              />
            </group>
            <group
              name="Chip_Pin_1016"
              position={[6.926, 0.352, -3.143]}
              userData={{ name: "Chip Pin 1.016" }}
            >
              <mesh
                name="Chip_Pin_1016_1"
                geometry={nodes.Chip_Pin_1016_1.geometry}
                material={materials["Chip Black"]}
              />
              <mesh
                name="Chip_Pin_1016_2"
                geometry={nodes.Chip_Pin_1016_2.geometry}
                material={materials["Chip Gold"]}
              />
            </group>
            <group
              name="Chip_Pin_1017"
              position={[6.691, 0.352, -3.143]}
              userData={{ name: "Chip Pin 1.017" }}
            >
              <mesh
                name="Chip_Pin_1017_1"
                geometry={nodes.Chip_Pin_1017_1.geometry}
                material={materials["Chip Black"]}
              />
              <mesh
                name="Chip_Pin_1017_2"
                geometry={nodes.Chip_Pin_1017_2.geometry}
                material={materials["Chip Gold"]}
              />
            </group>
            <group
              name="Chip_Pin_1018"
              position={[8.536, 0.352, -5.029]}
              rotation={[0, -1.571, 0]}
              userData={{ name: "Chip Pin 1.018" }}
            >
              <mesh
                name="Chip_Pin_1018_1"
                geometry={nodes.Chip_Pin_1018_1.geometry}
                material={materials["Chip Black"]}
              />
              <mesh
                name="Chip_Pin_1018_2"
                geometry={nodes.Chip_Pin_1018_2.geometry}
                material={materials["Chip Gold"]}
              />
            </group>
            <group
              name="Chip_Pin_1019"
              position={[8.536, 0.352, -4.911]}
              rotation={[0, -1.571, 0]}
              userData={{ name: "Chip Pin 1.019" }}
            >
              <mesh
                name="Chip_Pin_1019_1"
                geometry={nodes.Chip_Pin_1019_1.geometry}
                material={materials["Chip Black"]}
              />
              <mesh
                name="Chip_Pin_1019_2"
                geometry={nodes.Chip_Pin_1019_2.geometry}
                material={materials["Chip Gold"]}
              />
            </group>
            <group
              name="Chip_Pin_1020"
              position={[7.291, 0.352, -7.037]}
              userData={{ name: "Chip Pin 1.020" }}
            >
              <mesh
                name="Chip_Pin_1020_1"
                geometry={nodes.Chip_Pin_1020_1.geometry}
                material={materials["Chip Black"]}
              />
              <mesh
                name="Chip_Pin_1020_2"
                geometry={nodes.Chip_Pin_1020_2.geometry}
                material={materials["Chip Gold"]}
              />
            </group>
            <group
              name="Chip_Pin_1021"
              position={[7.291, 0.352, -7.556]}
              userData={{ name: "Chip Pin 1.021" }}
            >
              <mesh
                name="Chip_Pin_1021_1"
                geometry={nodes.Chip_Pin_1021_1.geometry}
                material={materials["Chip Black"]}
              />
              <mesh
                name="Chip_Pin_1021_2"
                geometry={nodes.Chip_Pin_1021_2.geometry}
                material={materials["Chip Gold"]}
              />
            </group>
            <group
              name="Chip_Pin_1022"
              position={[-0.76, 0.352, -8.928]}
              rotation={[0, -1.571, 0]}
              userData={{ name: "Chip Pin 1.022" }}
            >
              <mesh
                name="Chip_Pin_1022_1"
                geometry={nodes.Chip_Pin_1022_1.geometry}
                material={materials["Chip Black"]}
              />
              <mesh
                name="Chip_Pin_1022_2"
                geometry={nodes.Chip_Pin_1022_2.geometry}
                material={materials["Chip Gold"]}
              />
            </group>
            <group
              name="Chip_Pin_1023"
              position={[-3.362, 0.352, -10.165]}
              userData={{ name: "Chip Pin 1.023" }}
            >
              <mesh
                name="Chip_Pin_1023_1"
                geometry={nodes.Chip_Pin_1023_1.geometry}
                material={materials["Chip Black"]}
              />
              <mesh
                name="Chip_Pin_1023_2"
                geometry={nodes.Chip_Pin_1023_2.geometry}
                material={materials["Chip Gold"]}
              />
            </group>
            <group
              name="Chip_Pin_1024"
              position={[-3.245, 0.352, -10.165]}
              userData={{ name: "Chip Pin 1.024" }}
            >
              <mesh
                name="Chip_Pin_1024_1"
                geometry={nodes.Chip_Pin_1024_1.geometry}
                material={materials["Chip Black"]}
              />
              <mesh
                name="Chip_Pin_1024_2"
                geometry={nodes.Chip_Pin_1024_2.geometry}
                material={materials["Chip Gold"]}
              />
            </group>
            <group
              name="Chip_Pin_1025"
              position={[-0.878, 0.352, -8.928]}
              rotation={[0, -1.571, 0]}
              userData={{ name: "Chip Pin 1.025" }}
            >
              <mesh
                name="Chip_Pin_1025_1"
                geometry={nodes.Chip_Pin_1025_1.geometry}
                material={materials["Chip Black"]}
              />
              <mesh
                name="Chip_Pin_1025_2"
                geometry={nodes.Chip_Pin_1025_2.geometry}
                material={materials["Chip Gold"]}
              />
            </group>
            <group
              name="Chip_Pin_1026"
              position={[6.696, 0.352, -9.476]}
              userData={{ name: "Chip Pin 1.026" }}
            >
              <mesh
                name="Chip_Pin_1026_1"
                geometry={nodes.Chip_Pin_1026_1.geometry}
                material={materials["Chip Black"]}
              />
              <mesh
                name="Chip_Pin_1026_2"
                geometry={nodes.Chip_Pin_1026_2.geometry}
                material={materials["Chip Gold"]}
              />
            </group>
            <group
              name="Chip_Pin_1027"
              position={[6.578, 0.352, -9.476]}
              userData={{ name: "Chip Pin 1.027" }}
            >
              <mesh
                name="Chip_Pin_1027_1"
                geometry={nodes.Chip_Pin_1027_1.geometry}
                material={materials["Chip Black"]}
              />
              <mesh
                name="Chip_Pin_1027_2"
                geometry={nodes.Chip_Pin_1027_2.geometry}
                material={materials["Chip Gold"]}
              />
            </group>
            <group
              name="Cylinder_Cip"
              position={[-0.997, 0.314, -5.011]}
              userData={{ name: "Cylinder Cip" }}
            >
              <mesh
                name="Cylinder_Cip_1"
                geometry={nodes.Cylinder_Cip_1.geometry}
                material={materials["Chip Black"]}
              />
              {/* <mesh
                name="Cylinder_Cip_3"
                geometry={nodes.Cylinder_Cip_3.geometry}
                material={materials["Chip White"]}
              /> */}
            </group>
            <group
              name="Cylinder_Cip001"
              position={[0.472, 0.314, -3.375]}
              userData={{ name: "Cylinder Cip.001" }}
            >
              <mesh
                name="Cylinder_Cip001_1"
                geometry={nodes.Cylinder_Cip001_1.geometry}
                material={materials["Chip Black"]}
              />
              <mesh
                name="Cylinder_Cip001_2"
                geometry={nodes.Cylinder_Cip001_2.geometry}
                material={materials["Chip White"]}
              />
            </group>
            <group
              name="Isolators_2"
              position={[-0.313, 0.3, -7.591]}
              rotation={[Math.PI, 0, Math.PI]}
              userData={{ name: "Isolators 2" }}
            >
              <mesh
                name="Isolators_2_1"
                geometry={nodes.Isolators_2_1.geometry}
                material={materials["Chip Black"]}
              />
              <mesh
                name="Isolators_2_2"
                geometry={nodes.Isolators_2_2.geometry}
                material={materials["Chip Yellow"]}
              />
              <mesh
                name="Isolators_2_3"
                geometry={nodes.Isolators_2_3.geometry}
                material={materials["Chip Metal"]}
              />
            </group>
            <mesh
              name="Mother_Pin"
              geometry={nodes.Mother_Pin.geometry}
              material={materials["Chip Black"]}
              position={[0.597, 0.3, -5.131]}
              rotation={[0, -1.571, 0]}
              userData={{ name: "Mother Pin" }}
            />
            <mesh
              name="Mother_Pin001"
              geometry={nodes.Mother_Pin001.geometry}
              material={materials["Chip Black"]}
              position={[-0.92, 0.3, -2.912]}
              rotation={[0, -1.571, 0]}
              userData={{ name: "Mother Pin.001" }}
            />
            <mesh
              name="Mother_Pin002"
              geometry={nodes.Mother_Pin002.geometry}
              material={materials["Chip Black"]}
              position={[6.205, 0.3, -3.509]}
              userData={{ name: "Mother Pin.002" }}
            />
            <mesh
              name="Mother_Pin003"
              geometry={nodes.Mother_Pin003.geometry}
              material={materials["Chip Black"]}
              position={[6.187, 0.3, -9.472]}
              rotation={[0, -1.571, 0]}
              userData={{ name: "Mother Pin.003" }}
            />
            <group
              name="Tiny_Chip_1"
              position={[-0.093, 0.298, -4.811]}
              userData={{ name: "Tiny Chip 1" }}
            >
              <mesh
                name="Tiny_Chip_1_1"
                geometry={nodes.Tiny_Chip_1_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1_2"
                geometry={nodes.Tiny_Chip_1_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1001"
              position={[-0.533, 0.298, -4.804]}
              userData={{ name: "Tiny Chip 1.001" }}
            >
              <mesh
                name="Tiny_Chip_1001_1"
                geometry={nodes.Tiny_Chip_1001_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1001_2"
                geometry={nodes.Tiny_Chip_1001_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1002"
              position={[-0.093, 0.298, -5.106]}
              userData={{ name: "Tiny Chip 1.002" }}
            >
              <mesh
                name="Tiny_Chip_1002_1"
                geometry={nodes.Tiny_Chip_1002_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1002_2"
                geometry={nodes.Tiny_Chip_1002_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1003"
              position={[-0.533, 0.298, -5.106]}
              userData={{ name: "Tiny Chip 1.003" }}
            >
              <mesh
                name="Tiny_Chip_1003_1"
                geometry={nodes.Tiny_Chip_1003_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1003_2"
                geometry={nodes.Tiny_Chip_1003_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1004"
              position={[-0.093, 0.298, -5.407]}
              userData={{ name: "Tiny Chip 1.004" }}
            >
              <mesh
                name="Tiny_Chip_1004_1"
                geometry={nodes.Tiny_Chip_1004_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1004_2"
                geometry={nodes.Tiny_Chip_1004_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1005"
              position={[-0.533, 0.298, -5.407]}
              userData={{ name: "Tiny Chip 1.005" }}
            >
              <mesh
                name="Tiny_Chip_1005_1"
                geometry={nodes.Tiny_Chip_1005_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1005_2"
                geometry={nodes.Tiny_Chip_1005_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1006"
              position={[-0.093, 0.298, -5.711]}
              userData={{ name: "Tiny Chip 1.006" }}
            >
              <mesh
                name="Tiny_Chip_1006_1"
                geometry={nodes.Tiny_Chip_1006_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1006_2"
                geometry={nodes.Tiny_Chip_1006_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1007"
              position={[-0.533, 0.298, -5.711]}
              userData={{ name: "Tiny Chip 1.007" }}
            >
              <mesh
                name="Tiny_Chip_1007_1"
                geometry={nodes.Tiny_Chip_1007_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1007_2"
                geometry={nodes.Tiny_Chip_1007_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1008"
              position={[-0.093, 0.298, -6.001]}
              userData={{ name: "Tiny Chip 1.008" }}
            >
              <mesh
                name="Tiny_Chip_1008_1"
                geometry={nodes.Tiny_Chip_1008_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1008_2"
                geometry={nodes.Tiny_Chip_1008_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1009"
              position={[-0.533, 0.298, -6.001]}
              userData={{ name: "Tiny Chip 1.009" }}
            >
              <mesh
                name="Tiny_Chip_1009_1"
                geometry={nodes.Tiny_Chip_1009_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1009_2"
                geometry={nodes.Tiny_Chip_1009_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1010"
              position={[-0.093, 0.298, -6.302]}
              userData={{ name: "Tiny Chip 1.010" }}
            >
              <mesh
                name="Tiny_Chip_1010_1"
                geometry={nodes.Tiny_Chip_1010_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1010_2"
                geometry={nodes.Tiny_Chip_1010_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1011"
              position={[-0.533, 0.298, -6.302]}
              userData={{ name: "Tiny Chip 1.011" }}
            >
              <mesh
                name="Tiny_Chip_1011_1"
                geometry={nodes.Tiny_Chip_1011_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1011_2"
                geometry={nodes.Tiny_Chip_1011_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1012"
              position={[-0.093, 0.298, -6.606]}
              userData={{ name: "Tiny Chip 1.012" }}
            >
              <mesh
                name="Tiny_Chip_1012_1"
                geometry={nodes.Tiny_Chip_1012_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1012_2"
                geometry={nodes.Tiny_Chip_1012_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1013"
              position={[-0.533, 0.298, -6.606]}
              userData={{ name: "Tiny Chip 1.013" }}
            >
              <mesh
                name="Tiny_Chip_1013_1"
                geometry={nodes.Tiny_Chip_1013_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1013_2"
                geometry={nodes.Tiny_Chip_1013_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1014"
              position={[-0.093, 0.298, -6.911]}
              userData={{ name: "Tiny Chip 1.014" }}
            >
              <mesh
                name="Tiny_Chip_1014_1"
                geometry={nodes.Tiny_Chip_1014_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1014_2"
                geometry={nodes.Tiny_Chip_1014_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1015"
              position={[-0.533, 0.298, -6.911]}
              userData={{ name: "Tiny Chip 1.015" }}
            >
              <mesh
                name="Tiny_Chip_1015_1"
                geometry={nodes.Tiny_Chip_1015_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1015_2"
                geometry={nodes.Tiny_Chip_1015_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1016"
              position={[-0.533, 0.298, -7.374]}
              rotation={[Math.PI, 0, Math.PI]}
              userData={{ name: "Tiny Chip 1.016" }}
            >
              <mesh
                name="Tiny_Chip_1016_1"
                geometry={nodes.Tiny_Chip_1016_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1016_2"
                geometry={nodes.Tiny_Chip_1016_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1017"
              position={[-0.093, 0.298, -7.374]}
              rotation={[Math.PI, 0, Math.PI]}
              userData={{ name: "Tiny Chip 1.017" }}
            >
              <mesh
                name="Tiny_Chip_1017_1"
                geometry={nodes.Tiny_Chip_1017_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1017_2"
                geometry={nodes.Tiny_Chip_1017_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1018"
              position={[-0.533, 0.298, -7.674]}
              rotation={[Math.PI, 0, Math.PI]}
              userData={{ name: "Tiny Chip 1.018" }}
            >
              <mesh
                name="Tiny_Chip_1018_1"
                geometry={nodes.Tiny_Chip_1018_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1018_2"
                geometry={nodes.Tiny_Chip_1018_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1019"
              position={[-0.093, 0.298, -7.674]}
              rotation={[Math.PI, 0, Math.PI]}
              userData={{ name: "Tiny Chip 1.019" }}
            >
              <mesh
                name="Tiny_Chip_1019_1"
                geometry={nodes.Tiny_Chip_1019_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1019_2"
                geometry={nodes.Tiny_Chip_1019_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1020"
              position={[-0.533, 0.298, -7.976]}
              rotation={[Math.PI, 0, Math.PI]}
              userData={{ name: "Tiny Chip 1.020" }}
            >
              <mesh
                name="Tiny_Chip_1020_1"
                geometry={nodes.Tiny_Chip_1020_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1020_2"
                geometry={nodes.Tiny_Chip_1020_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1021"
              position={[-0.093, 0.298, -7.976]}
              rotation={[Math.PI, 0, Math.PI]}
              userData={{ name: "Tiny Chip 1.021" }}
            >
              <mesh
                name="Tiny_Chip_1021_1"
                geometry={nodes.Tiny_Chip_1021_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1021_2"
                geometry={nodes.Tiny_Chip_1021_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1022"
              position={[-0.533, 0.298, -8.282]}
              rotation={[Math.PI, 0, Math.PI]}
              userData={{ name: "Tiny Chip 1.022" }}
            >
              <mesh
                name="Tiny_Chip_1022_1"
                geometry={nodes.Tiny_Chip_1022_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1022_2"
                geometry={nodes.Tiny_Chip_1022_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1023"
              position={[-0.093, 0.298, -8.282]}
              rotation={[Math.PI, 0, Math.PI]}
              userData={{ name: "Tiny Chip 1.023" }}
            >
              <mesh
                name="Tiny_Chip_1023_1"
                geometry={nodes.Tiny_Chip_1023_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1023_2"
                geometry={nodes.Tiny_Chip_1023_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1024"
              position={[-0.533, 0.298, -8.558]}
              rotation={[Math.PI, 0, Math.PI]}
              userData={{ name: "Tiny Chip 1.024" }}
            >
              <mesh
                name="Tiny_Chip_1024_1"
                geometry={nodes.Tiny_Chip_1024_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1024_2"
                geometry={nodes.Tiny_Chip_1024_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1025"
              position={[-0.093, 0.298, -8.558]}
              rotation={[Math.PI, 0, Math.PI]}
              userData={{ name: "Tiny Chip 1.025" }}
            >
              <mesh
                name="Tiny_Chip_1025_1"
                geometry={nodes.Tiny_Chip_1025_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1025_2"
                geometry={nodes.Tiny_Chip_1025_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1026"
              position={[-0.533, 0.298, -8.862]}
              rotation={[Math.PI, 0, Math.PI]}
              userData={{ name: "Tiny Chip 1.026" }}
            >
              <mesh
                name="Tiny_Chip_1026_1"
                geometry={nodes.Tiny_Chip_1026_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1026_2"
                geometry={nodes.Tiny_Chip_1026_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1027"
              position={[-0.093, 0.298, -8.862]}
              rotation={[Math.PI, 0, Math.PI]}
              userData={{ name: "Tiny Chip 1.027" }}
            >
              <mesh
                name="Tiny_Chip_1027_1"
                geometry={nodes.Tiny_Chip_1027_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1027_2"
                geometry={nodes.Tiny_Chip_1027_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1028"
              position={[-0.533, 0.298, -9.163]}
              rotation={[Math.PI, 0, Math.PI]}
              userData={{ name: "Tiny Chip 1.028" }}
            >
              <mesh
                name="Tiny_Chip_1028_1"
                geometry={nodes.Tiny_Chip_1028_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1028_2"
                geometry={nodes.Tiny_Chip_1028_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1029"
              position={[-0.093, 0.298, -9.163]}
              rotation={[Math.PI, 0, Math.PI]}
              userData={{ name: "Tiny Chip 1.029" }}
            >
              <mesh
                name="Tiny_Chip_1029_1"
                geometry={nodes.Tiny_Chip_1029_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1029_2"
                geometry={nodes.Tiny_Chip_1029_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1030"
              position={[-0.533, 0.298, -9.468]}
              rotation={[Math.PI, 0, Math.PI]}
              userData={{ name: "Tiny Chip 1.030" }}
            >
              <mesh
                name="Tiny_Chip_1030_1"
                geometry={nodes.Tiny_Chip_1030_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1030_2"
                geometry={nodes.Tiny_Chip_1030_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1031"
              position={[-0.093, 0.298, -9.468]}
              rotation={[Math.PI, 0, Math.PI]}
              userData={{ name: "Tiny Chip 1.031" }}
            >
              <mesh
                name="Tiny_Chip_1031_1"
                geometry={nodes.Tiny_Chip_1031_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1031_2"
                geometry={nodes.Tiny_Chip_1031_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1032"
              position={[1.207, 0.298, -3.354]}
              rotation={[0, Math.PI / 2, 0]}
              scale={2.18}
              userData={{ name: "Tiny Chip 1.032" }}
            >
              <mesh
                name="Tiny_Chip_1032_1"
                geometry={nodes.Tiny_Chip_1032_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1032_2"
                geometry={nodes.Tiny_Chip_1032_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1033"
              position={[1.637, 0.298, -3.365]}
              rotation={[0, Math.PI / 2, 0]}
              userData={{ name: "Tiny Chip 1.033" }}
            >
              <mesh
                name="Tiny_Chip_1033_1"
                geometry={nodes.Tiny_Chip_1033_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1033_2"
                geometry={nodes.Tiny_Chip_1033_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1034"
              position={[1.632, 0.298, -3.23]}
              rotation={[0, Math.PI / 2, 0]}
              userData={{ name: "Tiny Chip 1.034" }}
            >
              <mesh
                name="Tiny_Chip_1034_1"
                geometry={nodes.Tiny_Chip_1034_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1034_2"
                geometry={nodes.Tiny_Chip_1034_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1035"
              position={[1.919, 0.298, -3.241]}
              rotation={[0, Math.PI / 2, 0]}
              scale={2.18}
              userData={{ name: "Tiny Chip 1.035" }}
            >
              <mesh
                name="Tiny_Chip_1035_1"
                geometry={nodes.Tiny_Chip_1035_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1035_2"
                geometry={nodes.Tiny_Chip_1035_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1036"
              position={[1.649, 0.298, -3.142]}
              userData={{ name: "Tiny Chip 1.036" }}
            >
              <mesh
                name="Tiny_Chip_1036_1"
                geometry={nodes.Tiny_Chip_1036_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1036_2"
                geometry={nodes.Tiny_Chip_1036_2.geometry}
                material={materials["Chip Black"]}
              />
            </group>
            <group
              name="Tiny_Chip_1037"
              position={[1.553, 0.298, -3.142]}
              userData={{ name: "Tiny Chip 1.037" }}
            >
              <mesh
                name="Tiny_Chip_1037_1"
                geometry={nodes.Tiny_Chip_1037_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1037_2"
                geometry={nodes.Tiny_Chip_1037_2.geometry}
                material={materials["Chip Black"]}
              />
            </group>
            <group
              name="Tiny_Chip_1038"
              position={[2.348, 0.298, -3.355]}
              rotation={[0, Math.PI / 2, 0]}
              scale={2.18}
              userData={{ name: "Tiny Chip 1.038" }}
            >
              <mesh
                name="Tiny_Chip_1038_1"
                geometry={nodes.Tiny_Chip_1038_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1038_2"
                geometry={nodes.Tiny_Chip_1038_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1039"
              position={[2.774, 0.298, -3.365]}
              rotation={[0, Math.PI / 2, 0]}
              userData={{ name: "Tiny Chip 1.039" }}
            >
              <mesh
                name="Tiny_Chip_1039_1"
                geometry={nodes.Tiny_Chip_1039_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1039_2"
                geometry={nodes.Tiny_Chip_1039_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1040"
              position={[2.777, 0.298, -3.25]}
              rotation={[0, Math.PI / 2, 0]}
              userData={{ name: "Tiny Chip 1.040" }}
            >
              <mesh
                name="Tiny_Chip_1040_1"
                geometry={nodes.Tiny_Chip_1040_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1040_2"
                geometry={nodes.Tiny_Chip_1040_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1041"
              position={[3.069, 0.298, -3.254]}
              rotation={[0, Math.PI / 2, 0]}
              scale={2.18}
              userData={{ name: "Tiny Chip 1.041" }}
            >
              <mesh
                name="Tiny_Chip_1041_1"
                geometry={nodes.Tiny_Chip_1041_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1041_2"
                geometry={nodes.Tiny_Chip_1041_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1042"
              position={[2.797, 0.298, -3.151]}
              userData={{ name: "Tiny Chip 1.042" }}
            >
              <mesh
                name="Tiny_Chip_1042_1"
                geometry={nodes.Tiny_Chip_1042_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1042_2"
                geometry={nodes.Tiny_Chip_1042_2.geometry}
                material={materials["Chip Black"]}
              />
            </group>
            <group
              name="Tiny_Chip_1043"
              position={[2.701, 0.298, -3.151]}
              userData={{ name: "Tiny Chip 1.043" }}
            >
              <mesh
                name="Tiny_Chip_1043_1"
                geometry={nodes.Tiny_Chip_1043_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1043_2"
                geometry={nodes.Tiny_Chip_1043_2.geometry}
                material={materials["Chip Black"]}
              />
            </group>
            <group
              name="Tiny_Chip_1044"
              position={[3.866, 0.298, -3.377]}
              rotation={[0, Math.PI / 2, 0]}
              scale={2.18}
              userData={{ name: "Tiny Chip 1.044" }}
            >
              <mesh
                name="Tiny_Chip_1044_1"
                geometry={nodes.Tiny_Chip_1044_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1044_2"
                geometry={nodes.Tiny_Chip_1044_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1045"
              position={[4.296, 0.298, -3.388]}
              rotation={[0, Math.PI / 2, 0]}
              userData={{ name: "Tiny Chip 1.045" }}
            >
              <mesh
                name="Tiny_Chip_1045_1"
                geometry={nodes.Tiny_Chip_1045_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1045_2"
                geometry={nodes.Tiny_Chip_1045_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1046"
              position={[4.291, 0.298, -3.253]}
              rotation={[0, Math.PI / 2, 0]}
              userData={{ name: "Tiny Chip 1.046" }}
            >
              <mesh
                name="Tiny_Chip_1046_1"
                geometry={nodes.Tiny_Chip_1046_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1046_2"
                geometry={nodes.Tiny_Chip_1046_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1047"
              position={[4.586, 0.298, -3.283]}
              rotation={[0, Math.PI / 2, 0]}
              scale={2.18}
              userData={{ name: "Tiny Chip 1.047" }}
            >
              <mesh
                name="Tiny_Chip_1047_1"
                geometry={nodes.Tiny_Chip_1047_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1047_2"
                geometry={nodes.Tiny_Chip_1047_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1048"
              position={[4.308, 0.298, -3.165]}
              userData={{ name: "Tiny Chip 1.048" }}
            >
              <mesh
                name="Tiny_Chip_1048_1"
                geometry={nodes.Tiny_Chip_1048_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1048_2"
                geometry={nodes.Tiny_Chip_1048_2.geometry}
                material={materials["Chip Black"]}
              />
            </group>
            <group
              name="Tiny_Chip_1049"
              position={[4.212, 0.298, -3.165]}
              userData={{ name: "Tiny Chip 1.049" }}
            >
              <mesh
                name="Tiny_Chip_1049_1"
                geometry={nodes.Tiny_Chip_1049_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1049_2"
                geometry={nodes.Tiny_Chip_1049_2.geometry}
                material={materials["Chip Black"]}
              />
            </group>
            <group
              name="Tiny_Chip_1050"
              position={[-4.23, 0.298, -10.323]}
              rotation={[0, Math.PI / 2, 0]}
              scale={2.18}
              userData={{ name: "Tiny Chip 1.050" }}
            >
              <mesh
                name="Tiny_Chip_1050_1"
                geometry={nodes.Tiny_Chip_1050_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1050_2"
                geometry={nodes.Tiny_Chip_1050_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1051"
              position={[-3.791, 0.298, -10.333]}
              rotation={[0, Math.PI / 2, 0]}
              userData={{ name: "Tiny Chip 1.051" }}
            >
              <mesh
                name="Tiny_Chip_1051_1"
                geometry={nodes.Tiny_Chip_1051_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1051_2"
                geometry={nodes.Tiny_Chip_1051_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1052"
              position={[-3.794, 0.298, -10.211]}
              rotation={[0, Math.PI / 2, 0]}
              userData={{ name: "Tiny Chip 1.052" }}
            >
              <mesh
                name="Tiny_Chip_1052_1"
                geometry={nodes.Tiny_Chip_1052_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1052_2"
                geometry={nodes.Tiny_Chip_1052_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1053"
              position={[-3.498, 0.298, -10.198]}
              rotation={[0, Math.PI / 2, 0]}
              scale={2.18}
              userData={{ name: "Tiny Chip 1.053" }}
            >
              <mesh
                name="Tiny_Chip_1053_1"
                geometry={nodes.Tiny_Chip_1053_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1053_2"
                geometry={nodes.Tiny_Chip_1053_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1054"
              position={[-3.775, 0.298, -10.112]}
              userData={{ name: "Tiny Chip 1.054" }}
            >
              <mesh
                name="Tiny_Chip_1054_1"
                geometry={nodes.Tiny_Chip_1054_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1054_2"
                geometry={nodes.Tiny_Chip_1054_2.geometry}
                material={materials["Chip Black"]}
              />
            </group>
            <group
              name="Tiny_Chip_1055"
              position={[-3.87, 0.298, -10.112]}
              userData={{ name: "Tiny Chip 1.055" }}
            >
              <mesh
                name="Tiny_Chip_1055_1"
                geometry={nodes.Tiny_Chip_1055_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1055_2"
                geometry={nodes.Tiny_Chip_1055_2.geometry}
                material={materials["Chip Black"]}
              />
            </group>
            <group
              name="Tiny_Chip_1056"
              position={[0.644, 0.298, -3.799]}
              rotation={[0, Math.PI / 2, 0]}
              userData={{ name: "Tiny Chip 1.056" }}
            >
              <mesh
                name="Tiny_Chip_1056_1"
                geometry={nodes.Tiny_Chip_1056_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1056_2"
                geometry={nodes.Tiny_Chip_1056_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1057"
              position={[0.508, 0.298, -3.722]}
              userData={{ name: "Tiny Chip 1.057" }}
            >
              <mesh
                name="Tiny_Chip_1057_1"
                geometry={nodes.Tiny_Chip_1057_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1057_2"
                geometry={nodes.Tiny_Chip_1057_2.geometry}
                material={materials["Chip Black"]}
              />
            </group>
            <group
              name="Tiny_Chip_1058"
              position={[0.572, 0.298, -3.777]}
              userData={{ name: "Tiny Chip 1.058" }}
            >
              <mesh
                name="Tiny_Chip_1058_1"
                geometry={nodes.Tiny_Chip_1058_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1058_2"
                geometry={nodes.Tiny_Chip_1058_2.geometry}
                material={materials["Chip Black"]}
              />
            </group>
            <group
              name="Tiny_Chip_1059"
              position={[0.573, 0.298, -3.831]}
              userData={{ name: "Tiny Chip 1.059" }}
            >
              <mesh
                name="Tiny_Chip_1059_1"
                geometry={nodes.Tiny_Chip_1059_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1059_2"
                geometry={nodes.Tiny_Chip_1059_2.geometry}
                material={materials["Chip Black"]}
              />
            </group>
            <group
              name="Tiny_Chip_1060"
              position={[0.327, 0.298, -3.705]}
              rotation={[0, -1.571, 0]}
              userData={{ name: "Tiny Chip 1.060" }}
            >
              <mesh
                name="Tiny_Chip_1060_1"
                geometry={nodes.Tiny_Chip_1060_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1060_2"
                geometry={nodes.Tiny_Chip_1060_2.geometry}
                material={materials["Chip Black"]}
              />
            </group>
            <group
              name="Tiny_Chip_1061"
              position={[0.376, 0.298, -3.705]}
              rotation={[0, -1.571, 0]}
              userData={{ name: "Tiny Chip 1.061" }}
            >
              <mesh
                name="Tiny_Chip_1061_1"
                geometry={nodes.Tiny_Chip_1061_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1061_2"
                geometry={nodes.Tiny_Chip_1061_2.geometry}
                material={materials["Chip Black"]}
              />
            </group>
            <group
              name="Tiny_Chip_1062"
              position={[0.432, 0.298, -3.705]}
              rotation={[0, -1.571, 0]}
              userData={{ name: "Tiny Chip 1.062" }}
            >
              <mesh
                name="Tiny_Chip_1062_1"
                geometry={nodes.Tiny_Chip_1062_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1062_2"
                geometry={nodes.Tiny_Chip_1062_2.geometry}
                material={materials["Chip Black"]}
              />
            </group>
            <group
              name="Tiny_Chip_1063"
              position={[0.167, 0.298, -3.756]}
              rotation={[0, Math.PI / 2, 0]}
              userData={{ name: "Tiny Chip 1.063" }}
            >
              <mesh
                name="Tiny_Chip_1063_1"
                geometry={nodes.Tiny_Chip_1063_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1063_2"
                geometry={nodes.Tiny_Chip_1063_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1064"
              position={[0.435, 0.298, -3.967]}
              userData={{ name: "Tiny Chip 1.064" }}
            >
              <mesh
                name="Tiny_Chip_1064_1"
                geometry={nodes.Tiny_Chip_1064_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1064_2"
                geometry={nodes.Tiny_Chip_1064_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1065"
              position={[0.249, 0.298, -3.88]}
              rotation={[0, Math.PI / 2, 0]}
              scale={2.365}
              userData={{ name: "Tiny Chip 1.065" }}
            >
              <mesh
                name="Tiny_Chip_1065_1"
                geometry={nodes.Tiny_Chip_1065_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1065_2"
                geometry={nodes.Tiny_Chip_1065_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1066"
              position={[0.091, 0.298, -3.72]}
              rotation={[0, Math.PI / 2, 0]}
              scale={2.365}
              userData={{ name: "Tiny Chip 1.066" }}
            >
              <mesh
                name="Tiny_Chip_1066_1"
                geometry={nodes.Tiny_Chip_1066_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1066_2"
                geometry={nodes.Tiny_Chip_1066_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1067"
              position={[0.251, 0.298, -3.721]}
              rotation={[0, Math.PI / 2, 0]}
              scale={1.631}
              userData={{ name: "Tiny Chip 1.067" }}
            >
              <mesh
                name="Tiny_Chip_1067_1"
                geometry={nodes.Tiny_Chip_1067_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1067_2"
                geometry={nodes.Tiny_Chip_1067_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1068"
              position={[8.149, 0.298, -8.357]}
              rotation={[0, Math.PI / 2, 0]}
              userData={{ name: "Tiny Chip 1.068" }}
            >
              <mesh
                name="Tiny_Chip_1068_1"
                geometry={nodes.Tiny_Chip_1068_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1068_2"
                geometry={nodes.Tiny_Chip_1068_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1069"
              position={[8.257, 0.298, -8.363]}
              scale={2.167}
              userData={{ name: "Tiny Chip 1.069" }}
            >
              <mesh
                name="Tiny_Chip_1069_1"
                geometry={nodes.Tiny_Chip_1069_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1069_2"
                geometry={nodes.Tiny_Chip_1069_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1070"
              position={[7.99, 0.298, -8.348]}
              rotation={[-Math.PI, 0, -Math.PI]}
              userData={{ name: "Tiny Chip 1.070" }}
            >
              <mesh
                name="Tiny_Chip_1070_1"
                geometry={nodes.Tiny_Chip_1070_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1070_2"
                geometry={nodes.Tiny_Chip_1070_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1071"
              position={[7.916, 0.298, -8.371]}
              rotation={[0, 1.571, 0]}
              userData={{ name: "Tiny Chip 1.071" }}
            >
              <mesh
                name="Tiny_Chip_1071_1"
                geometry={nodes.Tiny_Chip_1071_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1071_2"
                geometry={nodes.Tiny_Chip_1071_2.geometry}
                material={materials["Chip Black"]}
              />
            </group>
            <group
              name="Tiny_Chip_1072"
              position={[6.821, 0.298, -8.704]}
              rotation={[0, Math.PI / 2, 0]}
              userData={{ name: "Tiny Chip 1.072" }}
            >
              <mesh
                name="Tiny_Chip_1072_1"
                geometry={nodes.Tiny_Chip_1072_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1072_2"
                geometry={nodes.Tiny_Chip_1072_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1073"
              position={[6.495, 0.298, -9.283]}
              userData={{ name: "Tiny Chip 1.073" }}
            >
              <mesh
                name="Tiny_Chip_1073_1"
                geometry={nodes.Tiny_Chip_1073_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1073_2"
                geometry={nodes.Tiny_Chip_1073_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1074"
              position={[6.495, 0.298, -9.233]}
              userData={{ name: "Tiny Chip 1.074" }}
            >
              <mesh
                name="Tiny_Chip_1074_1"
                geometry={nodes.Tiny_Chip_1074_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1074_2"
                geometry={nodes.Tiny_Chip_1074_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1075"
              position={[6.639, 0.298, -9.178]}
              userData={{ name: "Tiny Chip 1.075" }}
            >
              <mesh
                name="Tiny_Chip_1075_1"
                geometry={nodes.Tiny_Chip_1075_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1075_2"
                geometry={nodes.Tiny_Chip_1075_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1076"
              position={[6.495, 0.298, -9.178]}
              userData={{ name: "Tiny Chip 1.076" }}
            >
              <mesh
                name="Tiny_Chip_1076_1"
                geometry={nodes.Tiny_Chip_1076_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1076_2"
                geometry={nodes.Tiny_Chip_1076_2.geometry}
                material={materials["Chip Black"]}
              />
            </group>
            <group
              name="Tiny_Chip_1077"
              position={[6.602, 0.298, -5.225]}
              rotation={[0, 1.571, 0]}
              userData={{ name: "Tiny Chip 1.077" }}
            >
              <mesh
                name="Tiny_Chip_1077_1"
                geometry={nodes.Tiny_Chip_1077_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1077_2"
                geometry={nodes.Tiny_Chip_1077_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1078"
              position={[7.102, 0.298, -4.393]}
              rotation={[-Math.PI, 0, -Math.PI]}
              userData={{ name: "Tiny Chip 1.078" }}
            >
              <mesh
                name="Tiny_Chip_1078_1"
                geometry={nodes.Tiny_Chip_1078_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1078_2"
                geometry={nodes.Tiny_Chip_1078_2.geometry}
                material={materials["Chip Black"]}
              />
            </group>
            <group
              name="Tiny_Chip_1079"
              position={[7.197, 0.298, -4.393]}
              rotation={[-Math.PI, 0, -Math.PI]}
              userData={{ name: "Tiny Chip 1.079" }}
            >
              <mesh
                name="Tiny_Chip_1079_1"
                geometry={nodes.Tiny_Chip_1079_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1079_2"
                geometry={nodes.Tiny_Chip_1079_2.geometry}
                material={materials["Chip Black"]}
              />
            </group>
            <group
              name="Tiny_Chip_1080"
              position={[8.135, 0.298, -4.443]}
              rotation={[0, Math.PI / 2, 0]}
              userData={{ name: "Tiny Chip 1.080" }}
            >
              <mesh
                name="Tiny_Chip_1080_1"
                geometry={nodes.Tiny_Chip_1080_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1080_2"
                geometry={nodes.Tiny_Chip_1080_2.geometry}
                material={materials["Chip Black"]}
              />
            </group>
            <group
              name="Tiny_Chip_1081"
              position={[8.351, 0.298, -4.443]}
              rotation={[0, Math.PI / 2, 0]}
              userData={{ name: "Tiny Chip 1.081" }}
            >
              <mesh
                name="Tiny_Chip_1081_1"
                geometry={nodes.Tiny_Chip_1081_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1081_2"
                geometry={nodes.Tiny_Chip_1081_2.geometry}
                material={materials["Chip Black"]}
              />
            </group>
            <group
              name="Tiny_Chip_1082"
              position={[8.41, 0.298, -4.443]}
              rotation={[0, Math.PI / 2, 0]}
              userData={{ name: "Tiny Chip 1.082" }}
            >
              <mesh
                name="Tiny_Chip_1082_1"
                geometry={nodes.Tiny_Chip_1082_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1082_2"
                geometry={nodes.Tiny_Chip_1082_2.geometry}
                material={materials["Chip Black"]}
              />
            </group>
            <group
              name="Tiny_Chip_1083"
              position={[8.625, 0.298, -4.443]}
              rotation={[0, Math.PI / 2, 0]}
              userData={{ name: "Tiny Chip 1.083" }}
            >
              <mesh
                name="Tiny_Chip_1083_1"
                geometry={nodes.Tiny_Chip_1083_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1083_2"
                geometry={nodes.Tiny_Chip_1083_2.geometry}
                material={materials["Chip Black"]}
              />
            </group>
            <group
              name="Tiny_Chip_1084"
              position={[8.351, 0.298, -4.535]}
              rotation={[0, Math.PI / 2, 0]}
              userData={{ name: "Tiny Chip 1.084" }}
            >
              <mesh
                name="Tiny_Chip_1084_1"
                geometry={nodes.Tiny_Chip_1084_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1084_2"
                geometry={nodes.Tiny_Chip_1084_2.geometry}
                material={materials["Chip Black"]}
              />
            </group>
            <group
              name="Tiny_Chip_1085"
              position={[8.625, 0.298, -4.535]}
              rotation={[0, Math.PI / 2, 0]}
              userData={{ name: "Tiny Chip 1.085" }}
            >
              <mesh
                name="Tiny_Chip_1085_1"
                geometry={nodes.Tiny_Chip_1085_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1085_2"
                geometry={nodes.Tiny_Chip_1085_2.geometry}
                material={materials["Chip Black"]}
              />
            </group>
            <group
              name="Tiny_Chip_1086"
              position={[8.254, 0.298, -4.768]}
              rotation={[-Math.PI, 0, -Math.PI]}
              userData={{ name: "Tiny Chip 1.086" }}
            >
              <mesh
                name="Tiny_Chip_1086_1"
                geometry={nodes.Tiny_Chip_1086_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1086_2"
                geometry={nodes.Tiny_Chip_1086_2.geometry}
                material={materials["Chip Black"]}
              />
            </group>
            <group
              name="Tiny_Chip_1087"
              position={[6.652, 0.298, -5.225]}
              rotation={[0, 1.571, 0]}
              userData={{ name: "Tiny Chip 1.087" }}
            >
              <mesh
                name="Tiny_Chip_1087_1"
                geometry={nodes.Tiny_Chip_1087_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1087_2"
                geometry={nodes.Tiny_Chip_1087_2.geometry}
                material={materials["Chip Black"]}
              />
            </group>
            <group
              name="Tiny_Chip_1088"
              position={[6.705, 0.298, -5.225]}
              rotation={[0, 1.571, 0]}
              userData={{ name: "Tiny Chip 1.088" }}
            >
              <mesh
                name="Tiny_Chip_1088_1"
                geometry={nodes.Tiny_Chip_1088_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1088_2"
                geometry={nodes.Tiny_Chip_1088_2.geometry}
                material={materials["Chip Black"]}
              />
            </group>
            <group
              name="Tiny_Chip_1089"
              position={[6.602, 0.298, -5.508]}
              rotation={[0, 1.571, 0]}
              userData={{ name: "Tiny Chip 1.089" }}
            >
              <mesh
                name="Tiny_Chip_1089_1"
                geometry={nodes.Tiny_Chip_1089_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1089_2"
                geometry={nodes.Tiny_Chip_1089_2.geometry}
                material={materials["Chip Black"]}
              />
            </group>
            <group
              name="Tiny_Chip_1090"
              position={[6.652, 0.298, -5.508]}
              rotation={[0, 1.571, 0]}
              userData={{ name: "Tiny Chip 1.090" }}
            >
              <mesh
                name="Tiny_Chip_1090_1"
                geometry={nodes.Tiny_Chip_1090_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1090_2"
                geometry={nodes.Tiny_Chip_1090_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1091"
              position={[6.705, 0.298, -5.508]}
              rotation={[0, 1.571, 0]}
              userData={{ name: "Tiny Chip 1.091" }}
            >
              <mesh
                name="Tiny_Chip_1091_1"
                geometry={nodes.Tiny_Chip_1091_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1091_2"
                geometry={nodes.Tiny_Chip_1091_2.geometry}
                material={materials["Chip Black"]}
              />
            </group>
            <group
              name="Tiny_Chip_1092"
              position={[6.602, 0.298, -5.785]}
              rotation={[0, 1.571, 0]}
              userData={{ name: "Tiny Chip 1.092" }}
            >
              <mesh
                name="Tiny_Chip_1092_1"
                geometry={nodes.Tiny_Chip_1092_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1092_2"
                geometry={nodes.Tiny_Chip_1092_2.geometry}
                material={materials["Chip Brown"]}
              />
            </group>
            <group
              name="Tiny_Chip_1093"
              position={[6.652, 0.298, -5.785]}
              rotation={[0, 1.571, 0]}
              userData={{ name: "Tiny Chip 1.093" }}
            >
              <mesh
                name="Tiny_Chip_1093_1"
                geometry={nodes.Tiny_Chip_1093_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1093_2"
                geometry={nodes.Tiny_Chip_1093_2.geometry}
                material={materials["Chip Black"]}
              />
            </group>
            <group
              name="Tiny_Chip_1094"
              position={[6.719, 0.298, -5.785]}
              rotation={[0, 1.571, 0]}
              userData={{ name: "Tiny Chip 1.094" }}
            >
              <mesh
                name="Tiny_Chip_1094_1"
                geometry={nodes.Tiny_Chip_1094_1.geometry}
                material={materials["Chip Metal"]}
              />
              <mesh
                name="Tiny_Chip_1094_2"
                geometry={nodes.Tiny_Chip_1094_2.geometry}
                material={materials["Chip Black"]}
              />
            </group>
          </group>
          <mesh
            name="Atum_A5_Signal"
            renderOrder={RENDER_ORDERS["Atum A5 Signal"]}
            geometry={nodes.Atum_A5_Signal.geometry}
            material={materials["Fuse Signal"]}
            position={[-0.167, -0.8, -5.781]}
            scale={11.617}
            userData={{ name: "Atum A5 Signal" }}
          />
          <group
            name="Motherboard"
            position={[-0.167, -0.8, -5.781]}
            scale={11.617}
            userData={{ name: "Motherboard" }}
          >
            <mesh
              name="Motherboard_1"
              geometry={nodes.Motherboard_1.geometry}
              material={materials.Motherboard}
            />
            <mesh
              name="Motherboard_2"
              geometry={nodes.Motherboard_2.geometry}
              material={materials["Metal 1"]}
            />
            <mesh
              name="Motherboard_3"
              geometry={nodes.Motherboard_3.geometry}
              material={materials.Black}
            />
          </group>
        </group>
        <group
          name="Opto-Isolators"
          position={[-0.313, 9.374, -7.591]}
          userData={{ name: "Opto-Isolators" }}
        >
          <group
            name="Isolators"
            rotation={[Math.PI, 0, Math.PI]}
            userData={{ name: "Isolators" }}
          >
            <mesh
              name="Isolators_1"
              geometry={nodes.Isolators_1.geometry}
              material={materials["Chip Black"]}
            />
            <mesh
              name="Isolators_3"
              geometry={nodes.Isolators_3.geometry}
              material={materials["Chip Yellow"]}
            />
            {/* <mesh
              name="Isolators_4"
              geometry={nodes.Isolators_4.geometry}
              material={materials["Chip Metal"]}
            /> */}
          </group>
        </group>
        <group
          name="Self-disable_Units"
          position={[7.415, 11.275, -4.761]}
          userData={{ name: "Self-disable Units" }}
        >
          <group
            name="Chip_Element"
            position={[-0.408, -1.9, -0.842]}
            userData={{ name: "Chip Element" }}
          >
            <mesh
              name="Chip_Element_1"
              geometry={nodes.Chip_Element_1.geometry}
              material={materials["Chip White"]}
            />
            <mesh
              name="Chip_Element_2"
              geometry={nodes.Chip_Element_2.geometry}
              material={materials["Chip Black"]}
            />
            <mesh
              name="Chip_Element_3"
              geometry={nodes.Chip_Element_3.geometry}
              material={materials["Chip Metal"]}
            />
            <mesh
              name="Chip_Element_4"
              geometry={nodes.Chip_Element_4.geometry}
              material={materials["Metal 1"]}
            />
            <mesh
              name="Chip_Element_5"
              geometry={nodes.Chip_Element_5.geometry}
              material={materials.Black}
            />
          </group>
          <group
            name="Cylinder_Cip_2"
            position={[0.32, -1.9, 0]}
            userData={{ name: "Cylinder Cip 2" }}
          >
            <mesh
              name="Cylinder_Cip_2_1"
              geometry={nodes.Cylinder_Cip_2_1.geometry}
              material={materials["Chip Blue"]}
            />
            <mesh
              name="Cylinder_Cip_2_2"
              geometry={nodes.Cylinder_Cip_2_2.geometry}
              material={materials["Chip White"]}
            />
            <mesh
              name="Cylinder_Cip_2_3"
              geometry={nodes.Cylinder_Cip_2_3.geometry}
              material={materials["Chip Gray"]}
            />
          </group>
          <group
            name="Cylinder_Cip_2001"
            position={[-0.32, -1.9, 0]}
            userData={{ name: "Cylinder Cip 2.001" }}
          >
            <mesh
              name="Cylinder_Cip_2001_1"
              geometry={nodes.Cylinder_Cip_2001_1.geometry}
              material={materials["Chip Blue"]}
            />
            <mesh
              name="Cylinder_Cip_2001_2"
              geometry={nodes.Cylinder_Cip_2001_2.geometry}
              material={materials["Chip White"]}
            />
            <mesh
              name="Cylinder_Cip_2001_3"
              geometry={nodes.Cylinder_Cip_2001_3.geometry}
              material={materials["Chip Gray"]}
            />
          </group>
        </group>
        <group
          name="Atum_A5_Parent"
          position={[0, 9.074, 0]}
          userData={{ name: "Atum A5 Parent" }}
        >
          <mesh
            name="Atum_A5_Flap"
            geometry={nodes.Atum_A5_Flap.geometry}
            material={materials["Chip Brown"]}
            position={[-4.536, 0.49, -6.627]}
            userData={{ name: "Atum A5 Flap" }}
          />
          <group
            name="Atum_A5"
            position={[-4.536, 0.49, -6.627]}
            userData={{ name: "Atum A5" }}
          >
            <mesh
              name="Atum_A5_1"
              geometry={nodes.Atum_A5_1.geometry}
              material={materials["Atum A5"]}
            />
            <mesh
              name="Atum_A5_2"
              geometry={nodes.Atum_A5_2.geometry}
              material={materials["Chip Dark Blue"]}
            />
            <mesh
              name="Atum_A5_3"
              geometry={nodes.Atum_A5_3.geometry}
              material={materials["Chip Metal"]}
            />
            <mesh
              name="Atum_A5_4"
              geometry={nodes.Atum_A5_4.geometry}
              material={materials["Chip Black"]}
            />
            <mesh
              name="Atum_A5_5"
              geometry={nodes.Atum_A5_5.geometry}
              material={materials["Chip Yellow"]}
            />
            <mesh
              name="Atum_A5_6"
              geometry={nodes.Atum_A5_6.geometry}
              material={materials["Metal 1"]}
            />
            <mesh
              name="Atum_A5_7"
              geometry={nodes.Atum_A5_7.geometry}
              material={materials["Chip Dark Grey"]}
            />
            <mesh
              name="Atum_A5_8"
              geometry={nodes.Atum_A5_8.geometry}
              material={materials["Chip Green"]}
            />
            <mesh
              name="Atum_A5_9"
              geometry={nodes.Atum_A5_9.geometry}
              material={materials["Chip Gold"]}
            />
            <mesh
              name="Atum_A5_10"
              geometry={nodes.Atum_A5_10.geometry}
              material={materials.Black}
            />
            <mesh
              name="Atum_A5_11"
              geometry={nodes.Atum_A5_11.geometry}
              material={materials["Chip Brown"]}
            />
          </group>
        </group>
        <group
          name="Memory"
          position={[-3.007, 10.213, -6.578]}
          userData={{ name: "Memory" }}
        >
          <group
            name="Atum_A5_Memory"
            position={[0, -0.666, 0]}
            userData={{ name: "Atum A5 Memory" }}
          >
            <mesh
              name="Atum_A5_Memory_1"
              geometry={nodes.Atum_A5_Memory_1.geometry}
              material={materials["Atum A5"]}
            />
            <mesh
              name="Atum_A5_Memory_2"
              geometry={nodes.Atum_A5_Memory_2.geometry}
              material={materials["Chip Dark Blue"]}
            />
          </group>
        </group>
        <group
          name="DWM_1001_Parent"
          position={[0, 9.074, 0]}
          userData={{ name: "DWM 1001 Parent" }}
        >
          <group
            name="DWM_1001"
            position={[7.646, 0.427, -1.524]}
            rotation={[Math.PI, 0, Math.PI]}
            scale={0.602}
            userData={{ name: "DWM 1001" }}
          >
            <mesh
              name="DWM_1001001_1"
              geometry={nodes.DWM_1001001_1.geometry}
              material={materials["DWM 1001"]}
            />
            <mesh
              name="DWM_1001001_2"
              geometry={nodes.DWM_1001001_2.geometry}
              material={materials["Chip Green"]}
            />
            <mesh
              name="DWM_1001001_3"
              geometry={nodes.DWM_1001001_3.geometry}
              material={materials["Chip Dark Blue"]}
            />
            <mesh
              name="DWM_1001001_4"
              geometry={nodes.DWM_1001001_4.geometry}
              material={materials["Chip Metal"]}
            />
            <mesh
              name="DWM_1001001_5"
              geometry={nodes.DWM_1001001_5.geometry}
              material={materials["Chip Black"]}
            />
            <mesh
              name="DWM_1001001_6"
              geometry={nodes.DWM_1001001_6.geometry}
              material={materials["Chip Yellow"]}
            />
            <mesh
              name="DWM_1001001_7"
              geometry={nodes.DWM_1001001_7.geometry}
              material={materials["Metal 1"]}
            />
            <mesh
              name="DWM_1001001_8"
              geometry={nodes.DWM_1001001_8.geometry}
              material={materials.Black}
            />
          </group>
          <group
            name="DWM_1001001"
            position={[1.903, 0.427, -1.524]}
            rotation={[Math.PI, 0, Math.PI]}
            scale={0.602}
            userData={{ name: "DWM 1001.001" }}
          >
            <mesh
              name="DWM_1001_1"
              geometry={nodes.DWM_1001_1.geometry}
              material={materials["DWM 1001"]}
            />
            <mesh
              name="DWM_1001_2"
              geometry={nodes.DWM_1001_2.geometry}
              material={materials["Chip Green"]}
            />
            <mesh
              name="DWM_1001_3"
              geometry={nodes.DWM_1001_3.geometry}
              material={materials["Chip Dark Blue"]}
            />
            <mesh
              name="DWM_1001_4"
              geometry={nodes.DWM_1001_4.geometry}
              material={materials["Chip Metal"]}
            />
            <mesh
              name="DWM_1001_5"
              geometry={nodes.DWM_1001_5.geometry}
              material={materials["Chip Black"]}
            />
            <mesh
              name="DWM_1001_6"
              geometry={nodes.DWM_1001_6.geometry}
              material={materials["Chip Yellow"]}
            />
            <mesh
              name="DWM_1001_7"
              geometry={nodes.DWM_1001_7.geometry}
              material={materials["Metal 1"]}
            />
            <mesh
              name="DWM_1001_8"
              geometry={nodes.DWM_1001_8.geometry}
              material={materials.Black}
            />
          </group>
        </group>
        <group
          name="W5500_Parent"
          position={[0, 9.074, 0]}
          userData={{ name: "W5500 Parent" }}
        >
          <group
            name="W5500-Ethernet_1"
            position={[-6.772, 0.278, -10.418]}
            rotation={[Math.PI, 0, Math.PI]}
            userData={{ name: "W5500-Ethernet 1" }}
          >
            <mesh
              name="W5500-Ethernet_2_1"
              geometry={nodes["W5500-Ethernet_2_1"].geometry}
              material={materials["W5500-Ethernet"]}
            />
            <mesh
              name="W5500-Ethernet_2_2"
              geometry={nodes["W5500-Ethernet_2_2"].geometry}
              material={materials["Chip Metal"]}
            />
            <mesh
              name="W5500-Ethernet_2_3"
              geometry={nodes["W5500-Ethernet_2_3"].geometry}
              material={materials["Chip Black"]}
            />
            <mesh
              name="W5500-Ethernet_2_4"
              geometry={nodes["W5500-Ethernet_2_4"].geometry}
              material={materials["Chip Yello 2"]}
            />
            <mesh
              name="W5500-Ethernet_2_5"
              geometry={nodes["W5500-Ethernet_2_5"].geometry}
              material={materials["Chip Dark Grey"]}
            />
            <mesh
              name="W5500-Ethernet_2_6"
              geometry={nodes["W5500-Ethernet_2_6"].geometry}
              material={materials["Chip Dark Blue"]}
            />
            <mesh
              name="W5500-Ethernet_2_7"
              geometry={nodes["W5500-Ethernet_2_7"].geometry}
              material={materials["Metal 1"]}
            />
            <mesh
              name="W5500-Ethernet_2_8"
              geometry={nodes["W5500-Ethernet_2_8"].geometry}
              material={materials.Black}
            />
          </group>
          <group
            name="W5500-Ethernet_2"
            position={[5.837, 0.278, -1.682]}
            rotation={[0, -1.571, 0]}
            userData={{ name: "W5500-Ethernet 2" }}
          >
            <mesh
              name="W5500-Ethernet_1_1"
              geometry={nodes["W5500-Ethernet_1_1"].geometry}
              material={materials["W5500-Ethernet"]}
            />
            <mesh
              name="W5500-Ethernet_1_2"
              geometry={nodes["W5500-Ethernet_1_2"].geometry}
              material={materials["Chip Metal"]}
            />
            <mesh
              name="W5500-Ethernet_1_3"
              geometry={nodes["W5500-Ethernet_1_3"].geometry}
              material={materials["Chip Black"]}
            />
            <mesh
              name="W5500-Ethernet_1_4"
              geometry={nodes["W5500-Ethernet_1_4"].geometry}
              material={materials["Chip Yello 2"]}
            />
            <mesh
              name="W5500-Ethernet_1_5"
              geometry={nodes["W5500-Ethernet_1_5"].geometry}
              material={materials["Chip Dark Grey"]}
            />
            <mesh
              name="W5500-Ethernet_1_6"
              geometry={nodes["W5500-Ethernet_1_6"].geometry}
              material={materials["Chip Dark Blue"]}
            />
            <mesh
              name="W5500-Ethernet_1_7"
              geometry={nodes["W5500-Ethernet_1_7"].geometry}
              material={materials["Metal 1"]}
            />
            <mesh
              name="W5500-Ethernet_1_8"
              geometry={nodes["W5500-Ethernet_1_8"].geometry}
              material={materials.Black}
            />
          </group>
        </group>
        <group
          name="Barrel_Jacks_Parrent"
          position={[0, 9.074, 0]}
          userData={{ name: "Barrel Jacks Parrent" }}
        >
          <group
            name="Barrel_Jack_1"
            position={[-1.426, 0.315, -10.692]}
            rotation={[0, -1.571, 0]}
            userData={{ name: "Barrel Jack 1" }}
          >
            <mesh
              name="Barrel_Jack_1_1"
              geometry={nodes.Barrel_Jack_1_1.geometry}
              material={materials["Chip Black"]}
            />
            <mesh
              name="Barrel_Jack_1_2"
              geometry={nodes.Barrel_Jack_1_2.geometry}
              material={materials["Chip Metal"]}
            />
          </group>
          <group
            name="Barrel_Jack_2"
            position={[-1.426, 0.315, -10.102]}
            rotation={[0, -1.571, 0]}
            userData={{ name: "Barrel Jack 2" }}
          >
            <mesh
              name="Barrel_Jack_2_1"
              geometry={nodes.Barrel_Jack_2_1.geometry}
              material={materials["Chip Black"]}
            />
            <mesh
              name="Barrel_Jack_2_2"
              geometry={nodes.Barrel_Jack_2_2.geometry}
              material={materials["Chip Metal"]}
            />
          </group>
          <group
            name="Barrel_Jack_3"
            position={[3.652, 0.396, -6.524]}
            rotation={[0, 1.571, 0]}
            userData={{ name: "Barrel Jack 3" }}
          >
            <mesh
              name="Barrel_Jack_3_1"
              geometry={nodes.Barrel_Jack_3_1.geometry}
              material={materials["Chip Black"]}
            />
            <mesh
              name="Barrel_Jack_3_2"
              geometry={nodes.Barrel_Jack_3_2.geometry}
              material={materials["Chip Metal"]}
            />
          </group>
        </group>
        <group
          name="CCG_30_Parent"
          position={[0, 9.074, 0]}
          userData={{ name: "CCG 30 Parent" }}
        >
          <group
            name="CCG_30"
            position={[-0.608, 0.329, -3.97]}
            rotation={[Math.PI, 0, Math.PI]}
            userData={{ name: "CCG 30" }}
          >
            <mesh
              name="CCG_30_1"
              geometry={nodes.CCG_30_1.geometry}
              material={materials["CCG 30"]}
            />
            <mesh
              name="CCG_30_2"
              geometry={nodes.CCG_30_2.geometry}
              material={materials["Chip Metal"]}
            />
          </group>
        </group>
        <group
          name="ECPIX_Parent"
          position={[0, 9.074, 0]}
          userData={{ name: "ECPIX Parent" }}
        >
          <group
            name="ECPIX"
            position={[3.721, 0.396, -6.573]}
            rotation={[0, 1.571, 0]}
            userData={{ name: "ECPIX" }}
          >
            <mesh
              name="ECPIX_1"
              geometry={nodes.ECPIX_1.geometry}
              material={materials.ECPIX}
            />
            <mesh
              name="ECPIX_2"
              geometry={nodes.ECPIX_2.geometry}
              material={materials["Chip White"]}
            />
            <mesh
              name="ECPIX_3"
              geometry={nodes.ECPIX_3.geometry}
              material={materials["Chip Black"]}
            />
            <mesh
              name="ECPIX_4"
              geometry={nodes.ECPIX_4.geometry}
              material={materials["Chip Metal"]}
            />
            <mesh
              name="ECPIX_5"
              geometry={nodes.ECPIX_5.geometry}
              material={materials["Chip Yellow"]}
            />
            <mesh
              name="ECPIX_6"
              geometry={nodes.ECPIX_6.geometry}
              material={materials["Metal 1"]}
            />
            <mesh
              name="ECPIX_7"
              geometry={nodes.ECPIX_7.geometry}
              material={materials.Black}
            />
          </group>
        </group>
        <group
          name="PH010"
          position={[0, 9.074, 0]}
          userData={{ name: "PH.010" }}
        >
          <group
            name="PH"
            position={[-5.101, 0.352, -10.762]}
            rotation={[0, 1.571, 0]}
            userData={{ name: "PH" }}
          >
            <mesh
              name="PH_1"
              geometry={nodes.PH_1.geometry}
              material={materials["Chip Black"]}
            />
            <mesh
              name="PH_2"
              geometry={nodes.PH_2.geometry}
              material={materials["Chip Gold"]}
            />
          </group>
          <group
            name="PH001"
            position={[5.518, 0.352, -3.35]}
            userData={{ name: "PH.001" }}
          >
            <mesh
              name="PH001_1"
              geometry={nodes.PH001_1.geometry}
              material={materials["Chip Black"]}
            />
            <mesh
              name="PH001_2"
              geometry={nodes.PH001_2.geometry}
              material={materials["Chip Gold"]}
            />
          </group>
        </group>
        <group
          name="ST_Parent"
          position={[0, 9.074, 0]}
          userData={{ name: "ST Parent" }}
        >
          <group
            name="ST"
            position={[-2.602, 0.2, -10.27]}
            userData={{ name: "ST" }}
          >
            <mesh
              name="ST_1"
              geometry={nodes.ST_1.geometry}
              material={materials["Chip Black"]}
            />
            <mesh
              name="ST_2"
              geometry={nodes.ST_2.geometry}
              material={materials["Chip Gold"]}
            />
          </group>
          <group
            name="ST001"
            position={[7.779, 0.2, -5.534]}
            userData={{ name: "ST.001" }}
          >
            <mesh
              name="ST001_1"
              geometry={nodes.ST001_1.geometry}
              material={materials["Chip Black"]}
            />
            <mesh
              name="ST001_2"
              geometry={nodes.ST001_2.geometry}
              material={materials["Chip Gold"]}
            />
          </group>
          <group
            name="ST002"
            position={[7.399, 0.2, -4.015]}
            userData={{ name: "ST.002" }}
          >
            <mesh
              name="ST002_1"
              geometry={nodes.ST002_1.geometry}
              material={materials["Chip Black"]}
            />
            <mesh
              name="ST002_2"
              geometry={nodes.ST002_2.geometry}
              material={materials["Chip Gold"]}
            />
          </group>
          <group
            name="ST003"
            position={[0.356, 0.2, -2.63]}
            userData={{ name: "ST.003" }}
          >
            <mesh
              name="ST003_1"
              geometry={nodes.ST003_1.geometry}
              material={materials["Chip Black"]}
            />
            <mesh
              name="ST003_2"
              geometry={nodes.ST003_2.geometry}
              material={materials["Chip Gold"]}
            />
          </group>
        </group>

        <group
          name="Needle_Attack"
          visible={true}
          //   visible={false}
          position={[0, 9.074, 0]}
          userData={{ name: "Needle Attack" }}
        >
          <group
            ref={needleProgressRef}
            name="Needle_Breach"
            visible={true}
            position={[0, 2, -15]}
            userData={{ name: "Needle Breach" }}
          >
            <mesh
              name="Needle_Breach_1"
              visible={true}
              geometry={nodes.Needle_Breach_1.geometry}
              material={materials["Metal 1"]}
            />
            <mesh
              // rotation={[Math.PI, 0, 0]}
              name="Needle_Breach_2"
              visible={true}
              geometry={nodes.Needle_Breach_2.geometry}
              material={materials.Warning}
            >
              {/* <meshStandardMaterial {...alertTextureProps} /> */}
            </mesh>
          </group>
          <group
            name="Needle_Fail"
            ref={needleFailRef}
            visible={true}
            position={[0, 2, -13.992]}
            userData={{ name: "Needle Fail" }}
          >
            <mesh
              name="Needle_Fail_1"
              visible={true}
              geometry={nodes.Needle_Fail_1.geometry}
              material={materials["Metal 1"]}
            />
            <mesh
              name="Needle_Fail_2"
              visible={true}
              geometry={nodes.Needle_Fail_2.geometry}
              material={materials.Warning}
            >
              {/* <meshStandardMaterial {...alertTextureProps} /> */}
            </mesh>
          </group>
        </group>

        <group
          name="Wires_Parent"
          position={[0, 13.515, 0]}
          userData={{ name: "Wires Parent" }}
        >
          <group
            name="Atum_A5_Wire"
            position={[-1.195, -4.041, -7.814]}
            userData={{ name: "Atum A5 Wire" }}
          >
            <mesh
              name="Atum_A5_Wire_1"
              geometry={nodes.Atum_A5_Wire_1.geometry}
              material={materials["Motherboard Cable"]}
            />
            <mesh
              name="Atum_A5_Wire_2"
              geometry={nodes.Atum_A5_Wire_2.geometry}
              material={materials.Black}
            />
          </group>
          <group
            name="Ethernet_Cable"
            position={[3.515, -1.682, -11.786]}
            userData={{ name: "Ethernet Cable" }}
          >
            {/* <mesh
              name="Ethernet_Cable_1"
              geometry={nodes.Ethernet_Cable_1.geometry}
              material={materials["Yellow Plastic"]}
              userData={{ name: "Ethernet Cable 1" }}
            /> */}
            {/* <mesh
              name="Ethernet_Cable_2"
              geometry={nodes.Ethernet_Cable_2.geometry}
              material={materials.Wires}
            /> */}
          </group>
          <group
            name="Ethernet_Port"
            position={[3.276, -3.139, -12.043]}
            userData={{ name: "Ethernet Port" }}
          >
            <mesh
              name="Ethernet_Port_1"
              geometry={nodes.Ethernet_Port_1.geometry}
              material={materials["Yellow Plastic"]}
            />
            <mesh
              name="Ethernet_Port_3"
              geometry={nodes.Ethernet_Port_3.geometry}
              material={materials.Black}
            />
            {/* <mesh
              name="Ethernet_Port_4"
              geometry={nodes.Ethernet_Port_4.geometry}
              material={materials.Gold}
            /> */}
          </group>
          <group
            name="Ethernet_Port_2"
            position={[3.276, -1.682, -12.043]}
            userData={{ name: "Ethernet Port 2" }}
          >
            <mesh
              name="Ethernet_Port_2_1"
              geometry={nodes.Ethernet_Port_2_1.geometry}
              material={materials["Yellow Plastic"]}
            />
            <mesh
              name="Ethernet_Port_2_2"
              geometry={nodes.Ethernet_Port_2_2.geometry}
              material={materials.Black}
            />
            <mesh
              name="Ethernet_Port_2_3"
              geometry={nodes.Ethernet_Port_2_3.geometry}
              material={materials.Gold}
            />
          </group>
          <group
            name="Power_Cord_Ends"
            position={[-4.05, -3.552, 1.261]}
            userData={{ name: "Power Cord Ends" }}
          >
            {/* <mesh
              name="Power_Cord_Ends_1"
              geometry={nodes.Power_Cord_Ends_1.geometry}
              material={materials.Wires}
            />
            <mesh
              name="Power_Cord_Ends_2"
              geometry={nodes.Power_Cord_Ends_2.geometry}
              material={materials.Black}
            />
            <mesh
              name="Power_Cord_Ends_3"
              geometry={nodes.Power_Cord_Ends_3.geometry}
              material={materials["Yellow Plastic"]}
              userData={{ name: "Ethernet Cord Ends" }}
            /> */}
          </group>
          {/* <group
            name="Router"
            position={[-5.037, -13.515, -16.893]}
            rotation={[0, -0.504, 0]}
            userData={{ name: "Router" }}
          >
            <mesh
              name="Router_1"
              geometry={nodes.Router_1.geometry}
              material={materials["White Plastic"]}
            />
            <mesh
              name="Router_2"
              geometry={nodes.Router_2.geometry}
              material={materials.Gray}
            />
            <mesh
              name="Router_3"
              geometry={nodes.Router_3.geometry}
              material={materials.Black}
            />
            <mesh
              name="Router_4"
              geometry={nodes.Router_4.geometry}
              material={materials.Red}
            />
            <mesh
              name="Router_5"
              geometry={nodes.Router_5.geometry}
              material={materials.Blue}
            />
            <mesh
              name="Router_6"
              geometry={nodes.Router_6.geometry}
              material={materials.Gold}
            />
            <mesh
              name="Router_7"
              geometry={nodes.Router_7.geometry}
              material={materials["Yellow Plastic"]}
            />
          </group> */}
          <group
            name="W5500_Wire_1"
            position={[5.515, -4.008, -3.102]}
            userData={{ name: "W5500 Wire 1" }}
          >
            <mesh
              name="W5500_Wire_1_1"
              geometry={nodes.W5500_Wire_1_1.geometry}
              material={materials["Motherboard Cable"]}
            />
            <mesh
              name="W5500_Wire_1_2"
              geometry={nodes.W5500_Wire_1_2.geometry}
              material={materials.Black}
            />
          </group>
          <group
            name="W5500_Wire_2"
            position={[-5.597, -4.033, -10.748]}
            rotation={[Math.PI, 0, Math.PI]}
            userData={{ name: "W5500 Wire 2" }}
          >
            <mesh
              name="W5500_Wire_2_1"
              geometry={nodes.W5500_Wire_2_1.geometry}
              material={materials.Black}
            />
            <mesh
              name="W5500_Wire_2_2"
              geometry={nodes.W5500_Wire_2_2.geometry}
              material={materials["Motherboard Cable"]}
            />
          </group>
          {/* <mesh
            name="Wires"
            geometry={nodes.Wires.geometry}
            material={materials.Wires}
            position={[-1.221, -3.794, -10.102]}
            userData={{ name: "Wires" }}
          /> */}
        </group>
        <group
          name="Armature"
          position={[4.759, 0.239, -27.665]}
          userData={{ name: "Armature" }}
        >
          <primitive
            object={nodes.Bone}
            name="Bone"
            rotation={[1.511, 0, -0.47]}
            userData={{ name: "Bone" }}
          >
            <primitive
              object={nodes.Bone001}
              name="Bone001"
              position={[0, 1.404, 0]}
              rotation={[-0.146, -0.074, 0.096]}
              userData={{ name: "Bone.001" }}
            >
              <primitive
                object={nodes.Bone002}
                name="Bone002"
                position={[0, 1.813, 0]}
                rotation={[-0.072, -0.028, 0.091]}
                userData={{ name: "Bone.002" }}
              >
                <primitive
                  object={nodes.Bone003}
                  name="Bone003"
                  position={[0, 1.931, 0]}
                  rotation={[-0.229, -0.064, 0.132]}
                  userData={{ name: "Bone.003" }}
                >
                  <primitive
                    object={nodes.Bone004}
                    name="Bone004"
                    position={[0, 1.822, 0]}
                    rotation={[-0.234, -0.032, 0.102]}
                    userData={{ name: "Bone.004" }}
                  >
                    <primitive
                      object={nodes.Bone005}
                      name="Bone005"
                      position={[0, 1.609, 0]}
                      rotation={[-0.108, -0.003, 0.036]}
                      userData={{ name: "Bone.005" }}
                    >
                      <primitive
                        object={nodes.Bone006}
                        name="Bone006"
                        position={[0, 2.007, 0]}
                        rotation={[-0.113, 0.001, -0.008]}
                        userData={{ name: "Bone.006" }}
                      >
                        <primitive
                          object={nodes.Bone007}
                          name="Bone007"
                          position={[0, 1.608, 0]}
                          rotation={[0.041, 0, 0.003]}
                          userData={{ name: "Bone.007" }}
                        >
                          <primitive
                            object={nodes.Bone008}
                            name="Bone008"
                            position={[0, 1.47, 0]}
                            rotation={[0.055, 0, 0]}
                            userData={{ name: "Bone.008" }}
                          >
                            <primitive
                              object={nodes.Bone009}
                              name="Bone009"
                              position={[0, 1.245, 0]}
                              rotation={[0.079, 0, 0.006]}
                              userData={{ name: "Bone.009" }}
                            >
                              <primitive
                                object={nodes.Bone010}
                                name="Bone010"
                                position={[0, 1.026, 0]}
                                rotation={[0.247, -0.002, -0.006]}
                                userData={{ name: "Bone.010" }}
                              >
                                <primitive
                                  object={nodes.Bone011}
                                  name="Bone011"
                                  position={[0, 0.922, 0]}
                                  rotation={[0.343, 0, -0.002]}
                                  userData={{ name: "Bone.011" }}
                                >
                                  <primitive
                                    object={nodes.Bone012}
                                    name="Bone012"
                                    position={[0, 1.316, 0]}
                                    rotation={[0.23, 0.001, 0.002]}
                                    userData={{ name: "Bone.012" }}
                                  />
                                </primitive>
                              </primitive>
                            </primitive>
                          </primitive>
                        </primitive>
                      </primitive>
                    </primitive>
                  </primitive>
                </primitive>
              </primitive>
            </primitive>
          </primitive>
          <primitive
            object={nodes.Bone013}
            name="Bone013"
            position={[-2.431, 0, -18.281]}
            rotation={[1.517, -0.002, -0.004]}
            userData={{ name: "Bone.013" }}
          />
          <primitive
            object={nodes.Bone014}
            name="Bone014"
            position={[-3.363, 0, -13.435]}
            rotation={[1.516, -0.002, 0.226]}
            userData={{ name: "Bone.014" }}
          />
          <primitive
            object={nodes.Bone015}
            name="Bone015"
            position={[-4.128, 0, -9.135]}
            rotation={[1.517, -0.002, -0.124]}
            userData={{ name: "Bone.015" }}
          />
          {/* <skinnedMesh
            name="Power_Plug_End"
            geometry={nodes.Power_Plug_End.geometry}
            material={materials.Black}
            skeleton={nodes.Power_Plug_End.skeleton}
            userData={{ name: "Power Plug End" }}
          /> */}
          {/* <skinnedMesh
            name="Power_Plug_Wire"
            geometry={nodes.Power_Plug_Wire.geometry}
            material={materials.Wires}
            skeleton={nodes.Power_Plug_Wire.skeleton}
            userData={{ name: "Power Plug Wire" }}
          /> */}
        </group>
        <group
          name="Atum_A5_Chip"
          position={[-4.533, 9.587, -6.703]}
          userData={{ name: "Atum A5 Chip" }}
        >
          <mesh
            name="1_Substrate"
            geometry={nodes["1_Substrate"].geometry}
            material={materials["1 Substrate"]}
            position={[-0.003, -0.03, 0]}
            renderOrder={RENDER_ORDERS["1 Substrate"]}
            scale={0.188}
            userData={{ name: "1 Substrate" }}
          />
          <mesh
            name="10_Met2"
            geometry={nodes["10_Met2"].geometry}
            material={materials["10 Met2"]}
            renderOrder={RENDER_ORDERS["10 Met2"]}
            position={[-0.003, -0.026, 0]}
            scale={0.188}
            userData={{ name: "10 Met2" }}
          />
          <mesh
            name="12_Met3"
            geometry={nodes["12_Met3"].geometry}
            material={materials["12 Met3"]}
            renderOrder={RENDER_ORDERS["12 Met3"]}
            position={[-0.003, -0.026, 0]}
            scale={0.188}
            userData={{ name: "12 Met3" }}
          />
          <mesh
            name="13_Via3"
            geometry={nodes["13_Via3"].geometry}
            material={materials["13 Via3"]}
            renderOrder={RENDER_ORDERS["13 Via3"]}
            position={[-0.003, -0.026, 0]}
            scale={0.188}
            userData={{ name: "13 Via3" }}
          />
          <mesh
            name="14_Met4"
            geometry={nodes["14_Met4"].geometry}
            material={materials["14 Met4"]}
            renderOrder={RENDER_ORDERS["14 Met4"]}
            position={[-0.003, -0.025, 0]}
            scale={0.188}
            userData={{ name: "14 Met4" }}
          />
          <mesh
            name="16_Tap"
            geometry={nodes["16_Tap"].geometry}
            material={materials["16 Tap"]}
            renderOrder={RENDER_ORDERS["16 Tap"]}
            position={[-0.003, -0.026, 0]}
            scale={0.188}
            userData={{ name: "16 Tap" }}
          />
          <mesh
            name="2_Nwell"
            geometry={nodes["2_Nwell"].geometry}
            material={materials["2 Nwell"]}
            renderOrder={RENDER_ORDERS["2 Nwell"]}
            position={[-0.003, -0.029, 0]}
            scale={0.188}
            userData={{ name: "2 Nwell" }}
          />
          <mesh
            name="4_Poly"
            geometry={nodes["4_Poly"].geometry}
            material={materials["4 Poly"]}
            renderOrder={RENDER_ORDERS["4 Poly"]}
            position={[-0.003, -0.029, 0]}
            scale={0.188}
            userData={{ name: "4 Poly" }}
          />
          <mesh
            name="5_Licon"
            geometry={nodes["5_Licon"].geometry}
            material={materials["5 Licon"]}
            renderOrder={RENDER_ORDERS["5 Licon"]}
            position={[-0.003, -0.028, 0]}
            scale={0.188}
            userData={{ name: "5 Licon" }}
          />
          <mesh
            ref={redHueRef3}
            name="6_Li1"
            geometry={nodes["6_Li1"].geometry}
            material={materials["6 Li1"]}
            renderOrder={RENDER_ORDERS["6 Li1"]}
            position={[-0.003, -0.028, 0]}
            scale={0.188}
            userData={{ name: "6 Li1" }}
          />
          <mesh
            name="7_Mcon"
            geometry={nodes["7_Mcon"].geometry}
            material={materials["7 Mcon"]}
            position={[-0.003, -0.028, 0]}
            scale={0.188}
            userData={{ name: "7 Mcon" }}
          />
          <mesh
            name="8_Met1"
            geometry={nodes["8_Met1"].geometry}
            material={materials["8 Met1"]}
            renderOrder={RENDER_ORDERS["8 Met1"]}
            position={[-0.003, -0.027, 0]}
            scale={0.188}
            userData={{ name: "8 Met1" }}
          />
          <mesh
            name="9_Via"
            geometry={nodes["9_Via"].geometry}
            material={materials["9 Via"]}
            renderOrder={RENDER_ORDERS["9 Via"]}
            position={[-0.003, -0.027, 0]}
            scale={0.188}
            userData={{ name: "9 Via" }}
          />
        </group>
        <group
          name="Atum_A5_Zoomed_Byte"
          position={[-4.533, 9.587, -6.703]}
          userData={{ name: "Atum A5 Zoomed Byte" }}
        >
          <mesh
            name="Fuse_Breaking_1"
            geometry={nodes.Fuse_Breaking_1.geometry}
            material={materials.Fuse}
            position={[-0.035, -0.028, -0.041]}
            scale={1.2}
            userData={{ name: "Fuse Breaking 1" }}
          />
          <mesh
            name="Fuse_Breaking_2"
            geometry={nodes.Fuse_Breaking_2.geometry}
            material={materials.Fuse}
            position={[-0.035, -0.028, -0.042]}
            scale={1.2}
            userData={{ name: "Fuse Breaking 2" }}
          />
          <mesh
            name="1_Substrate_Zoomed"
            geometry={nodes["1_Substrate_Zoomed"].geometry}
            material={materials["1 Substrate Zoomed"]}
            renderOrder={RENDER_ORDERS["1 Substrate Zoomed"]}
            position={[-0.04, -0.03, -0.048]}
            scale={0.188}
            userData={{ name: "1 Substrate Zoomed" }}
          />
          <mesh
            name="10_Met2_Zoomed"
            geometry={nodes["10_Met2_Zoomed"].geometry}
            material={materials["10 Met2 Zoomed"]}
            position={[-0.04, -0.026, -0.048]}
            scale={0.188}
            userData={{ name: "10 Met2 Zoomed" }}
            ref={flicker5Ref}
          />
          <mesh
            name="12_Met3_Zoomed"
            geometry={nodes["12_Met3_Zoomed"].geometry}
            material={materials["12 Met3 Zoomed"]}
            position={[-0.04, -0.0265, -0.048]}
            scale={0.188}
            userData={{ name: "12 Met3 Zoomed" }}
          />
          <mesh
            name="13_Via3_Zoomed"
            geometry={nodes["13_Via3_Zoomed"].geometry}
            material={materials["13 Via3 Zoomed"]}
            renderOrder={RENDER_ORDERS["13 Via3 Zoomed"]}
            position={[-0.04, -0.0268, -0.048]}
            scale={0.188}
            userData={{ name: "13 Via3 Zoomed" }}
          />
          <mesh
            name="14_Met4_Zoomed"
            geometry={nodes["14_Met4_Zoomed"].geometry}
            material={materials["14 Met4 Zoomed"]}
            renderOrder={RENDER_ORDERS["14 Met4 Zoomed"]}
            position={[-0.04, -0.025, -0.048]}
            scale={0.188}
            userData={{ name: "14 Met4 Zoomed" }}
          />
          <mesh
            ref={redHueRef2}
            name="16_Tap_Zoomed"
            geometry={nodes["16_Tap_Zoomed"].geometry}
            material={materials["16 Tap Zoomed"]}
            renderOrder={RENDER_ORDERS["16 Tap Zoomed"]}
            position={[-0.04, -0.026, -0.048]}
            scale={0.188}
            userData={{ name: "16 Tap Zoomed" }}
          />
          <mesh
            name="2_Nwell_Zoomed"
            geometry={nodes["2_Nwell_Zoomed"].geometry}
            material={materials["2 Nwell Zoomed"]}
            renderOrder={RENDER_ORDERS["2 Nwell Zoomed"]}
            position={[-0.04, -0.029, -0.048]}
            scale={0.188}
            userData={{ name: "2 Nwell Zoomed" }}
          />
          <mesh
            name="4_Poly_Zoomed"
            geometry={nodes["4_Poly_Zoomed"].geometry}
            material={materials["4 Poly Zoomed"]}
            renderOrder={RENDER_ORDERS["4 Poly Zoomed"]}
            position={[-0.04, -0.029, -0.048]}
            scale={0.188}
            userData={{ name: "4 Poly Zoomed" }}
          />
          <mesh
            name="5_Licon_Zoomed"
            geometry={nodes["5_Licon_Zoomed"].geometry}
            material={materials["5 Licon Zoomed"]}
            renderOrder={RENDER_ORDERS["5 Licon Zoomed"]}
            position={[-0.04, -0.028, -0.048]}
            scale={0.188}
            userData={{ name: "5 Licon Zoomed" }}
          />
          <mesh
            name="6_Li1_Zoomed"
            geometry={nodes["6_Li1_Zoomed"].geometry}
            material={materials["6 Li1 Zoomed"]}
            renderOrder={RENDER_ORDERS["6 Li1 Zoomed"]}
            position={[-0.04, -0.028, -0.048]}
            scale={0.188}
            userData={{ name: "6 Li1 Zoomed" }}
          />
          <mesh
            name="7_Mcon_Zoomed"
            geometry={nodes["7_Mcon_Zoomed"].geometry}
            material={materials["7 Mcon Zoomed"]}
            position={[-0.04, -0.028, -0.048]}
            scale={0.188}
            userData={{ name: "7 Mcon Zoomed" }}
          />
          <mesh
            ref={redHueRef1}
            name="8_Met1_Zoomed"
            geometry={nodes["8_Met1_Zoomed"].geometry}
            material={materials["8 Met1 Zoomed"]}
            renderOrder={RENDER_ORDERS["8 Met1 Zoomed"]}
            position={[-0.04, -0.027, -0.048]}
            scale={0.188}
            userData={{ name: "8 Met1 Zoomed" }}
          />
          <mesh
            name="9_Via_Zoomed"
            geometry={nodes["9_Via_Zoomed"].geometry}
            material={materials["9 Via Zoomed"]}
            renderOrder={RENDER_ORDERS["9 Via Zoomed"]}
            position={[-0.04, -0.027, -0.048]}
            scale={0.188}
            userData={{ name: "9 Via Zoomed" }}
          />
          <group
            name="Fuse_Details"
            position={[-0.034, -0.028, -0.041]}
            userData={{ name: "Fuse Details" }}
          >
            <mesh
              name="Fuse_Details_1"
              geometry={nodes.Fuse_Details_1.geometry}
              material={materials["Fuse White"]}
            />
            <mesh
              name="Fuse_Details_2"
              geometry={nodes.Fuse_Details_2.geometry}
              material={materials.Fuse}
            />
          </group>

          {/* <mesh
              ref={redHueRef}
              position={[-0.0348, -0.0276, -0.041]}
              scale={[0.00055, 0.003, 0.00055]}
              rotation={[Math.PI * 0.5, 0, 0]}
            >
              <cylinderGeometry />
              <meshStandardMaterial color="red" emissive={"#ff0000"} />
            </mesh> */}

          <group
            ref={fuseTopRef}
            name="Fuse_Top"
            position={[-0.035, -0.027, -0.041]}
            userData={{ name: "Fuse Top" }}
          >
            <mesh
              name="Fuse_Top_1"
              geometry={nodes.Fuse_Top_1.geometry}
              material={materials["Fuse White"]}
            />
            <mesh
              name="Fuse_Top_2"
              geometry={nodes.Fuse_Top_2.geometry}
              material={materials["Fuse Black"]}
            />
          </group>
        </group>
        <group
          name="Empty_Case"
          position={[-4.05, 0.889, 1.261]}
          userData={{ name: "Empty Case" }}
        >
          <mesh
            name="Empty_Case_1"
            geometry={nodes.Empty_Case_1.geometry}
            material={materials.Black}
          />
          <mesh
            name="Empty_Case_2"
            geometry={nodes.Empty_Case_2.geometry}
            material={materials["Yellow Plastic"]}
          />
          <mesh
            name="Empty_Case_3"
            geometry={nodes.Empty_Case_3.geometry}
            material={materials["Metal 1"]}
          />
          <mesh
            name="Empty_Case_4"
            geometry={nodes.Empty_Case_4.geometry}
            material={materials["Metal 3"]}
          />
          <mesh
            name="Empty_Case_5"
            geometry={nodes.Empty_Case_5.geometry}
            material={materials["Metal 3 Holes 1"]}
          />
          <mesh
            name="Empty_Case_6"
            geometry={nodes.Empty_Case_6.geometry}
            material={materials.Red}
          />
          <mesh
            name="Empty_Case_7"
            geometry={nodes.Empty_Case_7.geometry}
            material={materials.Green}
          />
          <mesh
            name="Empty_Case_8"
            geometry={nodes.Empty_Case_8.geometry}
            material={materials.Yellow}
          />
          <mesh
            name="Empty_Case_9"
            geometry={nodes.Empty_Case_9.geometry}
            material={materials["Blue Plastic"]}
          />
          <mesh
            name="Empty_Case_10"
            geometry={nodes.Empty_Case_10.geometry}
            material={materials["Lid Metal"]}
          />
          <mesh
            name="Empty_Case_11"
            geometry={nodes.Empty_Case_11.geometry}
            material={materials["Lid Metal Holes"]}
          />
          <mesh
            name="Empty_Case_12"
            geometry={nodes.Empty_Case_12.geometry}
            material={materials["Metal 2"]}
          />
          <mesh
            name="Empty_Case_13"
            geometry={nodes.Empty_Case_13.geometry}
            material={materials["Metal 1 Holes 1"]}
          />
          <mesh
            name="Empty_Case_14"
            geometry={nodes.Empty_Case_14.geometry}
            material={materials["Metal 1 Holes 2"]}
          />
          <mesh
            name="Empty_Case_15"
            geometry={nodes.Empty_Case_15.geometry}
            material={materials.Screws}
          />
          <mesh
            name="Empty_Case_16"
            geometry={nodes.Empty_Case_16.geometry}
            material={materials.Fans}
          />
          <mesh
            name="Empty_Case_17"
            geometry={nodes.Empty_Case_17.geometry}
            material={materials["Enclosure Back"]}
          />
          <mesh
            name="Empty_Case_19"
            geometry={nodes.Empty_Case_19.geometry}
            material={materials.Gold}
          />
        </group>
        <group name="Rack" userData={{ name: "Rack" }}>
          <mesh
            name="Rack_1"
            geometry={nodes.Rack_1.geometry}
            material={materials.Rack}
          />
          {/* <mesh
            name="Rack_2"
            geometry={nodes.Rack_2.geometry}
            material={materials["Metal 1"]}
          /> */}
        </group>
      </group>
    </group>
  );
}

useGLTF.preload(assets_prefix + "/test_10.gltf", true, true);
